import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/Auth';
import { usePermission } from '../../auth/usePermission';
import {
  Card,
  Grid,
  Modal,
  Box,
  CircularProgress,
  Snackbar,
  Alert,
  FormControlLabel,
  Checkbox,
  Skeleton,
  Divider,
  Typography,
  Tooltip,
  TextField,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import CallButton from "components/Softphone/CallButton";
import AssignToQueue from "components/AssignToQueue";
import AiScoreChip from "components/AiAssistant/AiScoreChip";
import NextBestActions from "components/AiAssistant/NextBestActions";
import MDButton from "components/MDButton";
import MDUserAvatar from "components/MDUserAvatar";

import ThemedCustomStepper from 'components/CustomStepper';
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import Activities from 'layouts/dashboard/components/Activities';
import InlineEditField from 'components/CustonEditableInlineField/CustomEditableInlineField';
import QualificationModal from 'components/LeadQulificationModal';
import ConvertLeadToContactModal from 'components/ConvertLeadToContactModal';
import ActivityModal from 'components/CustomModals/CustomActivityModal';
import { formatDate, toLocalDateTimeApi } from 'utils/DateTimeConversion';
import AttachmentUploadModal from 'components/CustomModals/AttachmentUploadModal';
import Attachments from 'layouts/dashboard/components/Attachments';
import LeadDetailButtons from 'components/CustomHeaderButtons/LeadCustomButtons';
import UnauthorizedAccess from 'components/StyledAccessDenied';
import DataLoadingSpinner from 'components/Spinners/DataLoadingSpinner';
import CustomFieldsSection from "components/CustomFields/CustomFieldsSection";
import colors from "assets/theme/base/colors";
import EmailModal from 'components/EmailModal/index.js';
import 'react-quill/dist/quill.snow.css';
import AttachmentIcon from '@mui/icons-material/Attachment';
import EventIcon from '@mui/icons-material/Event';
import { useCurrency } from '../../context/CurrencyContext';
import { CountrySelect, StateSelect, CitySelect } from 'components/GeographySelect';
import { downloadPrivateObject } from 'utils/S3';
import { frontendLogger } from '../../utils/logger';

import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import EchoComposer from 'components/EchoComposer/EchoComposer';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LockIcon from '@mui/icons-material/Lock';

import { useFeatureLicense } from 'hooks/useFeatureLicense';
import { useModuleEntitlements } from 'hooks/useModuleEntitlements';
import { useTravelUi } from 'hooks/useTravelUi';
import { useEditionFields } from 'hooks/useEditionFields';
import LeadPropertyRequirements from 'layouts/RealEstate/LeadPropertyRequirements';
import CallHistoryCard from 'components/CallRecording/CallHistoryCard';
import { useTheme } from "@mui/material/styles";
import { getApiErrorMessage, isModuleNotEntitledError } from 'utils/apiErrors';
import AddressAutocompleteInput from 'layouts/Travel/trips/stepForm/fields/AddressAutocompleteInput';
import StatusStepperConfirmDialog from 'components/StatusStepperConfirmDialog';
import { validatePersonOrEntityName } from 'utils/crmValidation';
import { useCurrencyCodes } from 'hooks/useCurrencyCodes';
import LeadFormModal from 'components/NewLeadModal';
import { RecordCallProvider } from "components/v2/RecordCallContext";
import RecordPageV2, { TabStack } from "components/v2/record/RecordPageV2";
import { HeroBadge } from "components/v2/record/RecordHero";
import { StatusBadge } from "layouts/crm/account360/RelatedList";
import ActivityTimeline from "layouts/crm/account360/ActivityTimeline";
import { relativeTime } from "layouts/crm/account360/format";
import { linkedValue } from "components/v2/ContactValue";
import { Briefcase, Building2, CalendarDays, CheckSquare, Clock, Gauge, History, LayoutGrid, Mail, MapPin, Megaphone, Paperclip, Phone, Star, Target, UserRound } from "lucide-react";
import UserLink from "components/v2/UserLink";
import { UiDetailSection, UiLabeledValue } from "components/v2/record/DetailSection";

const cleanMsg = (m) => String(m ?? '').replace(/^Value error,\s*/i, '');
const safeLog = async (fn) => {
  try { await fn(); } catch (e) { console.error('Logging failed:', e); }
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMAIL_OR_PHONE = 'Please provide either an email address or phone number.';

const has = (o, k) => Object.prototype.hasOwnProperty.call(o, k);
const isBlank = (v) =>
    v === undefined || v === null ||
    (typeof v === 'number' && Number.isNaN(v)) ||
    String(v).trim() === '';
const toNumber = (v) => Number(String(v).replace(/,/g, '').trim());

// Ignores common separators, then requires 7-15 digits with an optional leading +
const isValidPhone = (v) =>
    /^\+?\d{7,15}$/.test(String(v).trim().replace(/[\s\-().]/g, ''));

const isValidWebsite = (value) => {
  const v = String(value).trim();
  if (!v || /\s/.test(v)) return false;
  try {
    const url = new URL(/^https?:\/\//i.test(v) ? v : `https://${v}`);
    return url.hostname.includes('.') && !url.hostname.endsWith('.');
  } catch {
    return false;
  }
};

// Validates only the fields the user changed, so old bad data on a lead
// doesn't block unrelated edits. `merged` = lead + changes.
const validateLeadChanges = (merged, changed) => {
  const errors = {};

  if (has(changed, 'last_name')) {
    if (isBlank(merged.last_name)) errors.last_name = 'Last name is required.';
    else {
      const e = validatePersonOrEntityName(merged.last_name, 'Last name');
      if (e) errors.last_name = e;
    }
  }
  if (has(changed, 'first_name') && !isBlank(merged.first_name)) {
    const e = validatePersonOrEntityName(merged.first_name, 'First name');
    if (e) errors.first_name = e;
  }
  if ((has(changed, 'email') || has(changed, 'phone')) && isBlank(merged.email) && isBlank(merged.phone)) {
    errors.email = EMAIL_OR_PHONE;
    errors.phone = EMAIL_OR_PHONE;
  }
  if (has(changed, 'email') && !isBlank(merged.email) && !EMAIL_RE.test(String(merged.email).trim())) {
    errors.email = 'Please enter a valid email address (e.g., name@company.com).';
  }
  if (has(changed, 'phone') && !isBlank(merged.phone) && !isValidPhone(merged.phone)) {
    errors.phone = 'Please enter a valid phone number (digits, optionally starting with +).';
  }
  if (has(changed, 'website') && !isBlank(merged.website) && !isValidWebsite(merged.website)) {
    errors.website = 'Please enter a valid website URL (e.g., www.example.com).';
  }
  if (has(changed, 'number_of_employees') && !isBlank(merged.number_of_employees)) {
    const n = toNumber(merged.number_of_employees);
    if (!Number.isInteger(n) || n < 0) errors.number_of_employees = 'Please enter a whole number of 0 or more.';
  }
  if (has(changed, 'annual_revenue') && !isBlank(merged.annual_revenue)) {
    const n = toNumber(merged.annual_revenue);
    if (!Number.isFinite(n)) errors.annual_revenue = 'Please enter a valid monetary amount.';
    else if (n < 0) errors.annual_revenue = 'Annual revenue cannot be less than 0.';
  }
  return errors;
};

// Blank strings -> null, numeric strings -> numbers (matches the create modal)
const normalizeLeadChanges = (changes) => {
  const out = { ...changes };
  ['email', 'phone', 'mobile', 'website'].forEach((k) => {
    if (has(out, k)) out[k] = isBlank(out[k]) ? null : String(out[k]).trim();
  });
  ['annual_revenue', 'number_of_employees'].forEach((k) => {
    if (has(out, k)) out[k] = isBlank(out[k]) ? null : toNumber(out[k]);
  });
  return out;
};

// Turns any axios error into { fieldErrors, message }
const mapApiError = (error) => {
  const status = error?.response?.status;
  const detail = error?.response?.data?.detail;
  const fieldErrors = {};
  let message = 'Something went wrong. Please try again.';

  if (typeof detail === 'string') {
    message = cleanMsg(detail);
    if ([400, 409, 422].includes(status) && /email/i.test(message)) fieldErrors.email = message;
  } else if (Array.isArray(detail)) {
    message = detail
        .map((entry) => {
          const field = Array.isArray(entry?.loc)
              ? entry.loc.filter((p) => p !== 'body')[0]
              : entry?.field;
          const msg = cleanMsg(entry?.msg || entry?.message || 'Invalid value');
          if (field === undefined) {
            if (/email or phone/i.test(msg)) {
              fieldErrors.email = fieldErrors.email || msg;
              fieldErrors.phone = fieldErrors.phone || msg;
            }
          } else if (!fieldErrors[field]) {
            fieldErrors[field] = msg;
          }
          return msg;
        })
        .join(' ');
  } else if (detail && typeof detail === 'object') {
    message = cleanMsg(detail.message || detail.msg || message);
    if (detail.field) fieldErrors[detail.field] = message;
  } else if (!error?.response) {
    message = 'Could not reach the server. Please check your connection.';
  }
  return { fieldErrors, message };
};


// Add LeadDetailsSkeleton component
const LeadDetailsSkeleton = () => (
  <MDBox p={3}>
    <Grid container spacing={3}>
      {/* Simulate Basic Information section */}
      {[...Array(6)].map((_, index) => (
        <Grid item xs={12} md={6} key={`skeleton-field-${index}`}>
          <Skeleton variant="text" width="30%" height={20} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="100%" height={40} />
        </Grid>
      ))}

      <Grid item xs={12}>
        <Divider sx={{ my: 3 }} />
      </Grid>

      {/* Simulate Additional Details section */}
      {[...Array(4)].map((_, index) => (
        <Grid item xs={12} md={6} key={`skeleton-additional-${index}`}>
          <Skeleton variant="text" width="30%" height={20} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="100%" height={40} />
        </Grid>
      ))}

      <Grid item xs={12}>
        <Divider sx={{ my: 3 }} />
      </Grid>

      {/* Simulate Audit Information section */}
      {[...Array(4)].map((_, index) => (
        <Grid item xs={12} md={6} key={`skeleton-audit-${index}`}>
          <Skeleton variant="text" width="30%" height={20} sx={{ mb: 1 }} />
          <Skeleton variant="rectangular" width="100%" height={40} />
        </Grid>
      ))}
    </Grid>
  </MDBox>
);

function LeadDetail() {
  const { user, axiosInstance } = useAuth();
  const { isSupportUser, canDelete, canEdit, canCreate } = usePermission('leads');
  // UI v2 record layout (design-reference "Sales · Deal record").
  const isV2 = useTheme()?.pearlUi === 'v2';
  const { loading: licenseLoading } = useFeatureLicense();
  const {
    isModuleEnabled,
    loading: entitlementsLoading,
  } = useModuleEntitlements();
  const hasTasksModule = isModuleEnabled('TASKS');
  const hasProductsModule = isModuleEnabled('PRODUCTS');
  // Travel UI needs the TRAVEL edition and the company travel toggle.
  const { showTravel: showTripField } = useTravelUi();
  // Real Estate lead fields are tagged to the REAL_ESTATE edition.
  const { isFieldVisible } = useEditionFields();
  const showPropertyRequirements = isFieldVisible('leads', 're_purpose');
  // Keep Echo active in Lead module to match other modules.
  const echoFeatureEnabled = true;

  // Add state for feed items
  const [feedItems, setFeedItems] = useState([]);
  const [isFeedLoading, setIsFeedLoading] = useState(false);

  const { currency, formatCurrency } = useCurrency();
  const { currencies } = useCurrencyCodes();
  const { id } = useParams();
  console.log(`Lead ID: ${id}`);

  const navigate = useNavigate();

  const location = useLocation();
  const leadData = location.state?.leadData;
  const [lead, setLead] = useState(leadData || {});

  const [products, setProducts] = useState([]);

  const [isActivityModalOpen, setIsActivityModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [activities, setActivities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [updatedFields, setUpdatedFields] = useState({});
  const [deleteRelated, setDeleteRelated] = useState(false);
  const [newActivity, setNewActivity] = useState({
    lead_id: id,
    type: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
  });
  const [activeStep, setActiveStep] = useState(0);
  const leadStatus = [
    'New',
    'In Progress',
    'Qualified',
    'Disqualified',
  ];
  const steps = ['New', 'In Progress', 'Qualified', 'Disqualified'];
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const isLeadLocked = lead.lead_status === "Qualified" || lead.lead_status === "Disqualified";
  const canConvertToContact = lead.lead_status === "Disqualified" && !lead.contact_id;
  const [isQualificationModalOpen, setIsQualificationModalOpen] = useState(false);
  const [isConvertToContactOpen, setIsConvertToContactOpen] = useState(false);
  const [isConvertToContactLoading, setIsConvertToContactLoading] = useState(false);
  const [qualificationData, setQualificationData] = useState({
    value: 0,
    currency: null,
    stage: 'Qualification',
    probability: 0,
  });
  const [formErrors, setFormErrors] = useState({});

  const [agents, setAgents] = useState([]);
  const [isCloning, setIsCloning] = useState(false);

  const [attachments, setAttachments] = useState([]);
  const [isAttachmentModalOpen, setIsAttachmentModalOpen] = useState(false);

  const [finalState, setFinalState] = useState(null);

  const [isEditLoading, setIsEditLoading] = useState(false);
  const [isCloneLoading, setIsCloneLoading] = useState(false);
  const [isCloneModalOpen, setIsCloneModalOpen] = useState(false);
  const defaultCurrency = useMemo(
    () => currencies.find((c) => c.code === currency) || currencies[0] || null,
    [currencies, currency]
  );
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [isSaveLoading, setIsSaveLoading] = useState(false);
  const [isCancelLoading, setIsCancelLoading] = useState(false); // Added for Cancel button
  const [isEmailLoading, setIsEmailLoading] = useState(false);   // Added for Email button
  const [isUnauthorized, setIsUnauthorized] = useState(false);
  const [customFields, setCustomFields] = useState([]);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [newEmail, setNewEmail] = useState({
    to: '',
    cc: '',
    bcc: '',
    subject: '',
    content: ''
  });
  const [isGeneratingEmail, setIsGeneratingEmail] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [emailContext, setEmailContext] = useState({});
  const [emailAttachments, setEmailAttachments] = useState([]);


  const customFieldsRef = useRef(null); // Added to state declarations
  const [isDetailsLoading, setIsDetailsLoading] = useState(false); // Add this new state
  const [isActivitiesLoading, setIsActivitiesLoading] = useState(false);
  const [sectionOrder, setSectionOrder] = useState([]);
  const [hiddenSections, setHiddenSections] = useState([]);
  const [loadingSections, setLoadingSections] = useState(true);

  const entityType = 'leads'; // Since this is LeadDetail component - use plural to match backend
  const entityId = id; // Use whatever variable holds your lead ID

  const currencyOptions = useMemo(() => {
    const opts = (currencies || []).map((c) => ({
      value: c.code,
      label: `${c.name} (${c.code})`,
    }));
    // Keep a lead's saved currency selectable even if it's missing from the list
    const saved = lead?.currency;
    if (saved && !opts.some((o) => o.value === saved)) {
      opts.unshift({ value: saved, label: saved });
    }
    return opts;
  }, [currencies, lead?.currency]);

  // State for cached names
  const [nameCache, setNameCache] = useState({});

  // State for editing feed items
  const [editingFeedItem, setEditingFeedItem] = useState(null);
  const [editFeedContent, setEditFeedContent] = useState('');
  const [isEditingFeed, setIsEditingFeed] = useState(false);

  // State for echo modal
  const [isEchoModalOpen, setIsEchoModalOpen] = useState(false);
  const [newEchoContent, setNewEchoContent] = useState('');
  const [isCreatingEcho, setIsCreatingEcho] = useState(false);

  // State for delete confirmation
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const [statusStepConfirm, setStatusStepConfirm] = useState({ open: false, stepIndex: null });
  const [isStatusStepConfirmLoading, setIsStatusStepConfirmLoading] = useState(false);


  // Function to fetch case name
  const fetchCaseName = async (caseId) => {
    if (nameCache[`case_${caseId}`]) {
      return nameCache[`case_${caseId}`];
    }

    try {
      const response = await axiosInstance.get(`/cases/${caseId}`);
      const caseName = response.data.case_number || `Case #${caseId}`;
      setNameCache(prev => ({ ...prev, [`case_${caseId}`]: caseName }));
      return caseName;
    } catch (error) {
      console.error('Error fetching case name:', error);
      return `Case #${caseId}`;
    }
  };

  // Fetch global preferences for section order/hidden
  useEffect(() => {
    const fetchGlobalPreferences = async () => {
      try {
        const res = await axiosInstance.get('/preferences/global');
        const prefs = res.data?.section_preferences?.lead;
        if (prefs) {
          if (prefs.order) setSectionOrder(prefs.order);
          if (prefs.hidden) setHiddenSections(prefs.hidden);
        } else {
          // fallback to default order
          setSectionOrder([
            'Basic Information',
            'Additional Details',
            'Custom Field Information',
            'Audit Information',
          ]);
          setHiddenSections([]);
        }
      } catch (e) {
        // fallback to default order
        setSectionOrder([
          'Basic Information',
          'Additional Details',
          'Custom Field Information',
          'Audit Information',
        ]);
        setHiddenSections([]);
      } finally {
        setLoadingSections(false);
      }
    };
    fetchGlobalPreferences();
    // eslint-disable-next-line
  }, []);

  // Compose visible sections in order
  const visibleSections = sectionOrder
    .filter(key => !hiddenSections.includes(key));

  useEffect(() => {
    const initializePage = async () => {
      setIsLoading(true);
      try {
        // First, try to fetch lead data
        await fetchLeadData();

        // Core lead detail data — not gated by TASKS/PRODUCTS modules
        await Promise.allSettled([
          fetchAgents(),
          fetchAttachments(),
          fetchFeedItems(),
        ]);

      } catch (error) {
        console.error('Error initializing page:', error);
        // Module entitlement 403s must not be treated as lead access denial
        if (error.response?.status === 403 && !isModuleNotEntitledError(error)) {
          setIsUnauthorized(true);
          setSnackbar({
            open: true,
            message: "You don't have permission to access this lead",
            severity: 'error',
          });
          // Optionally redirect after a delay
          setTimeout(() => {
            navigate('/leads');
          }, 3000);
        } else if (!isModuleNotEntitledError(error)) {
          setSnackbar({
            open: true,
            message: 'Error loading lead data. Please try again later.',
            severity: 'error',
          });
        }
      }
      finally {
        setIsLoading(false);
      }
    };

    initializePage();
  }, [id]);

  // Load module-gated data only after entitlements are known
  useEffect(() => {
    if (entitlementsLoading || !id) return;

    if (hasTasksModule) {
      fetchActivities();
    } else {
      setActivities([]);
    }

    fetchInterestOptions();
  }, [id, entitlementsLoading, hasTasksModule, hasProductsModule, showTripField]);

  const fetchAttachments = async () => {
    const parentTable = 'leads';
    const parentId = id;
    try {
      const response = await axiosInstance.get('/attachments/', {
        params: {
          parent_table: parentTable,
          parent_id: parentId
        }
      });

      setAttachments(response.data || []);

    } catch (error) {
      if (isModuleNotEntitledError(error)) {
        setAttachments([]);
        return;
      }
      console.error('Error fetching attachments:', error);
      const errorMessage = getApiErrorMessage(error, 'Error fetching attachments');

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
        autoHideDuration: 5000
      });
    } finally {
      // If you have a loading state
      // setLoading(false);
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    try {
      await axiosInstance.delete(`/attachments/${attachmentId}`);
      setAttachments(attachments.filter((attachment) => attachment.id !== attachmentId));
      setSnackbar({
        open: true,
        message: 'Attachment deleted successfully!',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error deleting attachment:', error);

      // Log error
      await frontendLogger.logError(error, {
        context: 'Attachment deletion',
        attachmentId: attachmentId,
        leadId: id,
        user: user?.username,
        action: 'delete',
        timestamp: new Date().toISOString(),
      });

      setSnackbar({
        open: true,
        message: 'Error deleting attachment',
        severity: 'error',
      });
    }
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      if (!attachment.bucket_name || !attachment.storage_path) {
        throw new Error('Attachment storage metadata is invalid for strict R2 download');
      }
      await downloadPrivateObject({
        axiosInstance,
        attachmentId: attachment.id,
        fileName: attachment.file_name
      });
    } catch (error) {
      console.error('Error downloading attachment:', error);
      setSnackbar({
        open: true,
        message: 'Error downloading attachment',
        severity: 'error',
      });
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await axiosInstance.get('/users/');
      setAgents(response.data);
    } catch (error) {
      console.error('Error fetching agents:', error);
      setSnackbar({
        open: true,
        message: 'Error loading agents. Please try again later.',
        severity: 'error'
      });
    }
  };

  const handleEditToggle = async () => {
    if (isSupportUser) {
      setSnackbar({
        open: true,
        message: 'Support users are not allowed to edit leads.',
        severity: 'error',
      });
      return; // Exit the function early
    }
    setIsEditLoading(true); // Start loading
    try {
      if (isLeadLocked) {
        setSnackbar({
          open: true,
          message: 'This lead is locked and cannot be edited.',
          severity: 'warning',
        });
        return;
      }
      if (isEditing) {
        if (Object.keys(updatedFields).length > 0) {
          await handleSaveChanges();
        } else {
          setIsEditing(false);
          setSnackbar({
            open: true,
            message: 'No changes to save',
            severity: 'info',
          });
        }
      } else {
        setFormErrors({});
        setUpdatedFields({});
        setIsEditing(true);
      }
    } catch (error) {
      console.error('Error enabling edit mode:', error);
      setSnackbar({
        open: true,
        message: 'Error enabling edit mode',
        severity: 'error',
      });
    } finally {
      setIsEditLoading(false); // Stop loading
    }
  };

  const handleCloneLead = () => {
    if (isSupportUser) {
      setSnackbar({ open: true, message: 'Support users are not allowed to clone leads.', severity: 'error' });
      return;
    }
    if (!lead) {
      setSnackbar({ open: true, message: 'Cannot clone lead: Lead data not loaded', severity: 'error' });
      return;
    }
    setIsCloneModalOpen(true);
  };

  const handleCloneSave = async (formDataToSave) => {
    if (isSupportUser) {
      setSnackbar({ open: true, message: 'Support users are not allowed to clone leads.', severity: 'error' });
      throw new Error('Support users cannot clone leads');
    }
    setIsCloneLoading(true);
    try {
      const payload = {
        ...formDataToSave,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      const response = await axiosInstance.post('/leads/', payload);
      await safeLog(() => frontendLogger.logCreate('Lead', {
        id: response.data.id,
        action: 'clone',
        cloned_from: lead.id,
        user: user.username,
        timestamp: new Date().toISOString(),
      }));
      setSnackbar({ open: true, message: 'Lead cloned successfully!', severity: 'success' });
      setIsCloneModalOpen(false);
      return response.data;
    } catch (error) {
      console.error('Error cloning lead:', error);
      await safeLog(() => frontendLogger.logError(error, {
        context: 'Lead cloning',
        originalLeadId: lead.id,
        user: user?.username,
        action: 'clone',
        timestamp: new Date().toISOString(),
      }));
      // Re-throw so LeadFormModal can highlight email/phone field errors.
      throw error;
    } finally {
      setIsCloneLoading(false);
    }
  };

  const handleQualificationDataChange = (e) => {
    setQualificationData({
      ...qualificationData,
      [e.target.name]: e.target.value,
    });
  };

  // Travel on → trip packages; travel off → CRM products (if PRODUCTS module entitled).
  const fetchInterestOptions = async () => {
    try {
      if (showTripField) {
        const response = await axiosInstance.get('/trip-packages?status=active');
        const packages = Array.isArray(response.data) ? response.data : [];
        setProducts(
          packages.map((pkg) => ({
            id: pkg.id,
            name: pkg.title || pkg.name || `Package #${pkg.id}`,
          }))
        );
        return;
      }
      if (!hasProductsModule) {
        setProducts([]);
        return;
      }
      const response = await axiosInstance.get('/products/');
      setProducts(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      if (!isModuleNotEntitledError(error)) {
        console.error(
          showTripField ? 'Error fetching trip packages:' : 'Error fetching products:',
          error
        );
      }
      setProducts([]);
    }
  };

  const handleFieldUpdate = (field, value) => {
    if (field === 'lead_status') {
      const stepIndex = steps.indexOf(value);
      if (stepIndex === -1) return;
      // Status changes go through confirm (or qualification modal) — do not update yet
      handleStepClick(stepIndex);
    } else {
      console.log(`Updating field: ${field} with value:`, value);

      // Handle custom fields (fields starting with cf_)
      if (field.startsWith('cf_')) {
        let formattedValue = value;

        // Handle empty values
        if (value === '' || value === undefined) {
          formattedValue = null;
        }
        // Handle number fields - allow 0 values
        else if (typeof value === 'string' && !isNaN(value)) {
          const numValue = parseFloat(value);
          // Convert to number only if it's a valid number
          formattedValue = Number.isFinite(numValue) ? Number(numValue.toFixed(2)) : value;
        }
        // Handle boolean values
        else if (value === true || value === false) {
          formattedValue = value;
        }
        // Handle string values
        else if (value === null || typeof value === 'string') {
          formattedValue = value;
        }

        setUpdatedFields(prev => ({
          ...prev,
          [field]: formattedValue
        }));
      } else {
        // Handle regular fields
        setUpdatedFields(prev => ({
          ...prev,
          [field]: value
        }));
      }

      // Clear any errors for this field
      setFormErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        delete next.general;
        if ((field === 'email' || field === 'phone') && prev.email && prev.email === prev.phone) {
          delete next.email;
          delete next.phone;
        }
        return next;
      });
    }
  };


  useEffect(() => {
    const fetchCustomFields = async () => {
      try {
        const response = await axiosInstance.get(`/custom-fields/leads`);
        setCustomFields(response.data || []);
      } catch (error) {
        console.error('Error fetching custom fields:', error);
        setCustomFields([]);
      }
    };

    fetchCustomFields();
  }, []);


  const renderCustomFields = () => {
    if (!customFields || customFields.length === 0) {
      return null;
    }
    return (
      <CustomFieldsSection
        ref={customFieldsRef}
        objectType="leads"
        objectData={lead}
        isEditing={isEditing}
        onFieldUpdate={(field, value) => {
          handleFieldUpdate(field, value);
        }}
        onError={(field, error) => {
          if (error) {
            handleFieldError(field, error);
          }
        }}
        formErrors={formErrors}
      />
    );
  };

  const handleFieldError = (field, error) => {
    setFormErrors((prev) => {
      if (error) return { ...prev, [field]: error };
      const { [field]: _, ...rest } = prev;
      return rest;
    });
  };

  const formatApiError = (error, fallback) => {
    const detail = error?.response?.data?.detail;
    if (Array.isArray(detail)) {
      return detail
          .map((d) => `${(d.loc || []).filter((p) => p !== 'body').join('.')}: ${d.msg}`)
          .join(' | ');
    }
    if (typeof detail === 'string') return detail;
    return fallback;
  };

  // Blur check using the same rules as Save. Skips untouched fields so legacy
  // bad data on a lead doesn't raise an error (and block Save) just from tabbing through.
  const validateOnBlur = (field, value) => {
    if (!has(updatedFields, field)) return;
    const merged = { ...lead, ...updatedFields, [field]: value };
    handleFieldError(field, validateLeadChanges(merged, { [field]: value })[field] || '');
  };

  const handleQualificationConfirm = async (qualificationPayload) => {
    setIsQualificationModalOpen(false);
    setIsLoading(true);

    try {
      // ✅ Prepare conversion data
      const conversionData = {
        lead_id: parseInt(id, 10),
        value: Number(qualificationData.value) || 0,
        currency: qualificationPayload.currency,    // was qualificationData.currency?.code
        stage: qualificationData.stage,
        probability: Number.parseInt(qualificationData.probability, 10) || 0,
        estimated_closure_date: new Date(qualificationPayload.estimated_closure_date).toISOString(),
        assigned_to: qualificationPayload.assigned_to ?? null,

        // Account handling
        create_new_account: qualificationPayload.account.is_new,
        existing_account_id: qualificationPayload.account.is_new ? null : qualificationPayload.account.id,
        new_account_name: qualificationPayload.account.is_new ? qualificationPayload.account.name : null,

        // Contact handling
        create_new_contact: qualificationPayload.contact.is_new,
        existing_contact_id: qualificationPayload.contact.is_new ? null : qualificationPayload.contact.id,
        new_contact_first_name: qualificationPayload.contact.is_new ? qualificationPayload.contact.first_name : null,
        new_contact_last_name: qualificationPayload.contact.is_new ? qualificationPayload.contact.last_name : null,
      };

      // ✅ VALIDATION SECTION

      // New account name required
      if (conversionData.create_new_account && !conversionData.new_account_name) {
        setSnackbar({
          open: true,
          message: 'Account name is required when creating a new account.',
          severity: 'error',
        });
        setIsLoading(false);
        setIsQualificationModalOpen(true);
        return;
      }

      // Must select an account if not creating one
      if (!conversionData.create_new_account && !conversionData.existing_account_id) {
        setSnackbar({
          open: true,
          message: 'Please select an account or create a new one.',
          severity: 'error',
        });
        setIsLoading(false);
        setIsQualificationModalOpen(true);
        return;
      }

      // New contact last name required
      if (conversionData.create_new_contact && !conversionData.new_contact_last_name) {
        setSnackbar({
          open: true,
          message: 'Contact last name is required when creating a new contact.',
          severity: 'error',
        });
        setIsLoading(false);
        setIsQualificationModalOpen(true);
        return;
      }

      // Must select a contact if not creating one
      if (!conversionData.create_new_contact && !conversionData.existing_contact_id) {
        setSnackbar({
          open: true,
          message: 'Please select a contact or create a new one.',
          severity: 'error',
        });
        setIsLoading(false);
        setIsQualificationModalOpen(true);
        return;
      }

      // ✅ API CALL TO CONVERT LEAD
      const response = await axiosInstance.post(`/leads/${id}/convert`, conversionData);

      // ✅ Log Deal
      await frontendLogger.logCreate('Deal', {
        id: response.data.id,
        name: response.data.name,
        value: response.data.value,
        stage: response.data.stage,
        probability: response.data.probability,
        account_id: response.data.account_id,
        created_by: user.id,
        created_at: response.data.created_at || new Date().toISOString(),
        user: user.username,
      });

      // ✅ Log Account (if newly created)
      if (response.data.account_id && conversionData.create_new_account) {
        await frontendLogger.logCreate('Account', {
          id: response.data.account_id,
          name: conversionData.new_account_name,
          created_by: user.id,
          user: user.username,
          created_at: new Date().toISOString(),
        });
      }

      // ✅ Show success snackbar
      setSnackbar({
        open: true,
        message: 'Congrats! You have successfully qualified this lead.',
        severity: 'success',
      });

      // ✅ Redirect to new deal after delay
      setTimeout(() => {
        navigate(`/deals/${response.data.id}`);
      }, 2000);

    } catch (error) {
      console.error('Error qualifying lead:', error);

      // ✅ Show friendly error
      setSnackbar({
        open: true,
        message: formatApiError(error, 'Error qualifying lead. Please try again.'),
        severity: 'error',
      });

      setIsQualificationModalOpen(true);

    } finally {
      setIsLoading(false);
    }
  };



  const renderQualificationModal = () => (
    <QualificationModal
      isOpen={isQualificationModalOpen}
      onClose={() => setIsQualificationModalOpen(false)}
      qualificationData={qualificationData}
      handleQualificationDataChange={handleQualificationDataChange}
      handleQualificationConfirm={handleQualificationConfirm}
      lead={lead}
      axiosInstance={axiosInstance}
    />
  );

  const handleSaveChanges = async () => {
    if (isSaveLoading) return;

    if (Object.keys(updatedFields).length === 0) {
      setSnackbar({ open: true, message: 'No changes to save', severity: 'info' });
      setIsEditing(false);
      return;
    }

    const isCustomFieldsValid = customFieldsRef.current?.validateAllFields() ?? true;
    const clientErrors = validateLeadChanges({ ...lead, ...updatedFields }, updatedFields);
    const { general: _g, ...existingErrors } = formErrors;
    const allErrors = { ...existingErrors, ...clientErrors };

    if (Object.keys(allErrors).length > 0 || !isCustomFieldsValid) {
      setFormErrors(allErrors);
      setSnackbar({ open: true, message: 'Please correct the highlighted fields.', severity: 'warning' });
      scrollToFirstError();
      return;
    }

    // Button spinner, NOT isDetailsLoading: the skeleton unmounts the inline
    // editors, so a failed save would visually revert the user's edits.
    setIsSaveLoading(true);
    try {
      const payload = {
        ...normalizeLeadChanges(updatedFields),
        updated_at: new Date().toISOString(),
      };
      await axiosInstance.patch(`/leads/${lead.id}`, payload);

      try {
        await frontendLogger.logUpdate('Lead', {
          id: lead.id,
          updated_fields: Object.keys(updatedFields),
          changes: updatedFields,
          action: 'edit',
          user: user.username,
          timestamp: new Date().toISOString(),
        });
      } catch (logError) {
        console.error('Error logging lead update:', logError);
      }

      await fetchLeadData(); // also refreshes step and final state
      setUpdatedFields({});
      setFormErrors({});
      setSnackbar({ open: true, message: 'Lead updated successfully!', severity: 'success' });
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating lead:', error);
      try {
        await frontendLogger.logError(error, {
          context: 'Lead update',
          leadId: lead.id,
          updatedFields: Object.keys(updatedFields),
          user: user?.username,
          action: 'edit',
          timestamp: new Date().toISOString(),
        });
      } catch (logError) {
        console.error('Error logging update failure:', logError);
      }

      const { fieldErrors, message } = mapApiError(error);
      if (Object.keys(fieldErrors).length > 0) {
        setFormErrors((prev) => ({ ...prev, ...fieldErrors }));
        scrollToFirstError();
      }
      setSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setIsSaveLoading(false);
    }
  };

  const handleUserClick = (userId, userName) => {
    if (userId) {
      navigate(`/users/${userId}/profile`);
    } else {
      setSnackbar({
        open: true,
        message: `Unable to find user information for ${userName}.`,
        severity: 'info',
      });
    }
  };

  // Toggle functions for CC and BCC fields
  const toggleCc = () => setShowCc(!showCc);
  const toggleBcc = () => setShowBcc(!showBcc);

  // Function to handle Quill editor changes
  const handleQuillChange = (content) => {
    setNewEmail({ ...newEmail, content });
  };

  // Define Quill editor modules and formats
  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ color: [] }, { background: [] }],
      ['link', 'image'],
      ['clean'],
    ],
  };

  const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'color', 'background',
    'link', 'image',
  ];


  const handleToggleEmailModal = async () => {
    setIsEmailLoading(true); // Start loading
    try {
      if (!isEmailModalOpen) {
        if (!lead) {
          setSnackbar({
            open: true,
            message: 'Lead details not loaded. Please try again.',
            severity: 'error',
          });
          return;
        }
        if (!lead.email || lead.email.trim() === '') {
          setSnackbar({
            open: true,
            message: 'No email address linked to this lead. Please add an email address first.',
            severity: 'warning',
          });
          return;
        }

        const emailSubject = `Regarding ${lead.full_lead_name} - Lead Information`;
        const emailContent = ``;

        setNewEmail({
          to: lead.email,
          cc: '',
          bcc: '',
          subject: emailSubject,
          content: emailContent,
          from: user.email
        });

        setEmailContext({
          type: 'lead',
          data: lead,
          activities: activities,
          sender: user
        });

        setEmailAttachments([]);
        setIsEmailModalOpen(true);
      } else {
        setIsEmailModalOpen(false);
      }
    } catch (error) {
      console.error('Error preparing email data:', error);
      setSnackbar({
        open: true,
        message: 'Error preparing email data. Please try again.',
        severity: 'error',
      });
    } finally {
      setIsEmailLoading(false); // Stop loading
    }
  };


  // Function to generate email content using AI
  const handleGenerateEmail = async () => {
    setIsGeneratingEmail(true);
    setEmailError('');

    try {
      // Check if sender's email is provided
      // if (!user?.email) {
      //   throw new Error('Sender email address is missing. Please update your profile with a valid email address.');
      // }

      // Implementation for AI email generation
      const context = emailContext;
      let generatedContent = '';
      let subject = '';

      if (context.type === 'lead') {
        const leadData = context.data;
        subject = `Lead Update: ${leadData.full_lead_name}`;
        generatedContent = `<p>Dear ${leadData.contact_person || 'Customer'},</p>
          <p>I hope this email finds you well. I'm reaching out regarding your lead with us.</p>
          <p>We value our partnership with ${leadData.full_lead_name} and wanted to provide you with an update on your account status.</p>
          <p>Here's a summary of your recent activity:</p>
          <ul>
            <li>Open Deals: ${context.deals?.length || 0}</li>
            <li>Recent Orders: ${context.orders?.length || 0}</li>
            <li>Active Cases: ${context.cases?.length || 0}</li>
          </ul>
          <p>If you have any questions or require any assistance, please don't hesitate to reach out.</p>
          <p>Looking forward to our continued collaboration.</p>
          <p>Best regards,<br>${user?.name || 'Sales Team'}</p>`;
      }

      // Set the email content before sending
      const emailToSend = {
        ...newEmail,
        subject: subject || newEmail.subject,
        content: generatedContent || newEmail.content,
        // from_email: user?.email,
        // from_name: user?.name || 'Sales Team'
      };

      setNewEmail(emailToSend);

      // Call the API to send the email
      const response = await axiosInstance.post('/emails/send', {
        // subject: emailToSend.subject,
        // content: emailToSend.content,
        // to_email: emailToSend.to,
        // to_name: emailToSend.to_name,
        // from_email: emailToSend.from_email,
        // from_name: emailToSend.from_name,
        context: context
      });

      if (response.status === 200) {
        // Success handling
        setSnackbar({ open: true, message: 'Email sent successfully!', severity: 'success' });
      } else {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error generating or sending email:', error);

      if (error.response) {
        console.error('Error data:', error.response.data);
        console.error('Error status:', error.response.status);
        console.error('Error headers:', error.response.headers);
        setEmailError(`Server error: ${error.response.data.detail || error.message}`);
      } else if (error.request) {
        console.error('Error request:', error.request);
        setEmailError('No response received from server. Please try again.');
      } else {
        console.error('Error message:', error.message);
        setEmailError(`Error: ${error.message}`);
      }
    } finally {
      setIsGeneratingEmail(false);
    }
  };

  // Function to send the email
  // In AccountDetail.js, modify the handleSendEmail function:

  // Add this helper function near other utility functions
  const stripHtmlTags = (html) => {
    if (!html) return '';
    // First replace <br>, <p>, and other block elements with newlines
    const textWithNewlines = html
      .replace(/<\/(p|div|br|h[1-6])>/gi, '\n')
      .replace(/<br\/?>/gi, '\n');
    // Then remove all HTML tags and decode HTML entities
    const div = document.createElement('div');
    div.innerHTML = textWithNewlines;
    return div.textContent || div.innerText || '';
  };

  // Update the handleSendEmail function
  const handleSendEmail = async () => {
    if (isSupportUser) {
      setSnackbar({
        open: true,
        message: 'Support users are not allowed to send emails.',
        severity: 'error',
      });
      return; // Exit the function early
    }
    setIsSendingEmail(true);
    try {
      if (!newEmail.to || !newEmail.subject || !newEmail.content) {
        const missingFields = [];
        if (!newEmail.to) missingFields.push('To');
        if (!newEmail.subject) missingFields.push('Subject');
        if (!newEmail.content) missingFields.push('Content');
        throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
      }

      const emailPayload = {
        to: newEmail.to,
        subject: newEmail.subject,
        content: newEmail.content,
        cc: newEmail.cc || '',
        bcc: newEmail.bcc || '',
        parent_table: 'leads',
        parent_table_id: id
      };

      // Send email
      const emailResponse = await axiosInstance.post('/emails/send', emailPayload);

      // After email is sent successfully, create an activity with local wall-clock
      // due_date (same as Add Activity) — avoid toISOString() UTC shift.
      const now = new Date();
      const cleanDescription = stripHtmlTags(newEmail.content);

      const activityPayload = {
        title: `Email: ${newEmail.subject}`,
        type: "Email",
        status: "Completed",
        description: cleanDescription,
        starttime: null,
        endtime: null,
        due_date: toLocalDateTimeApi(now),
        parent_table: "leads",
        parent_table_id: id,
        assigned_to: user.id,
        assigned_to_name: user.fullname,
        all_day: false,
        color: "#edc949"
      };

      // Create activity
      const activityResponse = await axiosInstance.post('/activities/', activityPayload);

      if (!activityResponse.data) {
        throw new Error('Failed to create activity record');
      }

      setSnackbar({
        open: true,
        message: 'Mail sent successfully',
        severity: 'success',
      });

      setEmailAttachments([]);
      setIsEmailModalOpen(false);

      // Refresh activities list
      await fetchActivities();

    } catch (error) {
      console.error('Error sending email or creating activity:', error);
      let errorMessage = 'Error sending email';

      if (error.response) {
        const { status, data } = error.response;
        errorMessage = typeof data.detail === 'string'
          ? data.detail
          : Array.isArray(data.detail)
            ? data.detail[0]?.msg || 'Unknown error'
            : 'Server error';

        if (status === 404 && data.detail === 'Email settings not found') {
          errorMessage = 'Email settings not configured. Please set up your email provider in settings.';
        } else if (status === 400) {
          errorMessage = 'Invalid request. Please check your input.';
        } else if (status === 401) {
          errorMessage = 'Authentication failed. Please log in again.';
        }
      } else if (error.request) {
        errorMessage = 'Failed to connect to the server';
      } else {
        errorMessage = error.message || 'Unknown error occurred';
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    } finally {
      setIsSendingEmail(false);
    }
  };

  const capitalizeFirstLetter = (str) => {
    if (!str) return '';
    if (typeof str !== 'string') return str;
    return str
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const detailsRef = useRef(null);
  const [scrollTick, setScrollTick] = useState(0);
  const scrollToFirstError = () => setScrollTick((t) => t + 1);

  useEffect(() => {
    if (!scrollTick) return;
    const root = detailsRef.current;
    if (!root) return;
    const first = Array.from(root.querySelectorAll('[aria-invalid="true"]'))
        .find((el) => el.offsetParent !== null);
    if (!first) return;
    first.scrollIntoView({ behavior: 'smooth', block: 'center' });
    first.focus({ preventScroll: true });
  }, [scrollTick]);

  const renderSection = (title, fields, children) => {
    // One body for both UIs: v2 shows it in a DetailSection, legacy keeps its card.
    const sectionBody = (
      <>
      <Grid container spacing={3}>
        {fields.filter((item) => !item.hidden).map((item) => {

          const fieldError = typeof formErrors[item.field] === 'string' ? formErrors[item.field] : '';

          return (
          <Grid item xs={12} md={6} key={item.field}>
            {['created_by_name', 'updated_by_name'].includes(item.field) ? (
              <UiLabeledValue label={item.label}>
              <MDBox>
                <MDTypography variant="button" fontWeight="regular" color="text">
                  <strong>{item.label}:</strong>
                </MDTypography>
                <MDTypography
                  component="span"
                  onClick={() => {
                    const userId = lead['created_by'] || lead['updated_by'];
                    handleUserClick(userId, lead[item.field]);
                  }}
                  variant="button"
                  color="info"
                  fontWeight="medium"
                  sx={{
                    cursor: 'pointer',
                    ml: 1
                  }}
                >
                  {capitalizeFirstLetter(lead[item.field] || '')}
                </MDTypography>
              </MDBox>
              </UiLabeledValue>
            ) : item.field === 'address' && isEditing && !isLeadLocked ? (
                <Box
                    sx={{
                      mt: 2.5, // ~20px: lines the top edge up with City/Country
                      '& .MuiOutlinedInput-root': {
                        minHeight: 60,          // match City/Country height
                        borderRadius: '12px',   // match their corner radius
                        alignItems: 'center',
                      },
                    }}
                >
                  <AddressAutocompleteInput
                      label={item.label}
                      placeholder="Enter address"
                      value={updatedFields.address ?? lead?.address ?? ''}
                      onChange={(nextAddress) => handleFieldUpdate('address', nextAddress)}
                      error={Boolean(formErrors.address)}
                      errorMessage={formErrors.address}
                  />
                </Box>
            ) : item.field === 'country' && isEditing && !isLeadLocked ? (
              <CountrySelect
                label={item.label}
                value={updatedFields.country ?? lead?.country ?? ''}
                margin="normal"
                onChange={(nextCountry) => {
                  setUpdatedFields((prev) => ({
                    ...prev,
                    country: nextCountry || '',
                    state: '',
                    city: '',
                  }));
                }}
              />
            ) : item.field === 'state' && isEditing && !isLeadLocked ? (
              <StateSelect
                label={item.label}
                country={updatedFields.country ?? lead?.country ?? ''}
                value={updatedFields.state ?? lead?.state ?? ''}
                margin="normal"
                onChange={(nextState) => {
                  setUpdatedFields((prev) => ({
                    ...prev,
                    state: nextState || '',
                    city: '',
                  }));
                }}
              />
            ) : item.field === 'city' && isEditing && !isLeadLocked ? (
              <CitySelect
                label={item.label}
                country={updatedFields.country ?? lead?.country ?? ''}
                state={updatedFields.state ?? lead?.state ?? ''}
                value={updatedFields.city ?? lead?.city ?? ''}
                margin="normal"
                onChange={(nextCity) => handleFieldUpdate('city', nextCity || '')}
              />
            ) : ['email', 'phone', 'website'].includes(item.field) && isEditing && !isLeadLocked ? (
                <TextField
                    fullWidth
                    label={item.label}
                    value={updatedFields[item.field] ?? lead?.[item.field] ?? ''}
                    onChange={(e) => handleFieldUpdate(item.field, e.target.value)}
                    onBlur={(e) => validateOnBlur(item.field, e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        validateOnBlur(item.field, e.target.value);
                      }
                    }}
                    error={Boolean(fieldError)}
                    helperText={fieldError}
                    required={
                        (item.field === 'email' && !lead?.phone) ||
                        (item.field === 'phone' && !lead?.email)
                    }
                />
            ) : item.field === 'annual_revenue' ? (
                <InlineEditField
                    label={item.label}
                    value={lead?.annual_revenue}
                    type="number"
                    onSave={(newValue) => {
                      handleFieldUpdate('annual_revenue', newValue);
                      // Instant feedback, like the modal's validation
                      const n = toNumber(newValue);
                      if (!isBlank(newValue) && (!Number.isFinite(n) || n < 0)) {
                        handleFieldError('annual_revenue', 'Annual revenue cannot be less than 0.');
                      }
                    }}
                    onError={(_, error) => handleFieldError(item.field, error)}
                    error={fieldError}
                    isEditing={isEditing && !isLeadLocked}
                    readOnly={(item.readOnly ?? false) || isLeadLocked}
                    renderValue={(v) =>
                        isBlank(v) ? '' : formatCurrency(v, lead?.currency || currency)
                    }
                />
            ) : (
              <InlineEditField
                label={item.label}
                value={lead?.[item.field]}
                options={
                  item.field === 'assigned_to'
                    ? agents.map(agent => ({ value: agent.id, label: agent.fullname }))
                    : item.options ?? null
                }
                onSave={(newValue) => handleFieldUpdate(item.field, newValue)}
                onError={(_, error) => handleFieldError(item.field, error)}
                error={fieldError}
                isEditing={isEditing && !isLeadLocked}
                readOnly={(item.readOnly ?? false) || isLeadLocked}
                isMultiSelect={item.isMultiSelect}
                renderValue={
                  item.field === 'assigned_to'
                    ? (value) => (
                      <MDTypography
                        component="span"
                        onClick={() => handleAgentClick(value)}
                        variant="button"
                        color="info"
                        fontWeight="medium"
                        sx={{
                          cursor: 'pointer'
                        }}
                      >
                        {capitalizeFirstLetter(agents.find(agent => agent.id === value)?.fullname || 'Unassigned')}
                      </MDTypography>
                    )
                    : item.field.includes('_at') || item.field === 'last_activity'
                      ? (value) => formatDate(value, 'datetime')
                      : item.field === 'email'
                        ? (value) => value
                        : item.field === 'notes' || item.field === 'currency'
                          ? (value) => value // Show as-is for notes and currency
                          : item.preserveCase
                            ? (value) => value // Show as-is for first_name and last_name
                            : (value) => capitalizeFirstLetter(value)
                }
                required={
                  item.field === 'last_name' ||
                  (item.field === 'email' && !lead?.phone) ||
                  (item.field === 'phone' && !lead?.email)
                }
                helperText={
                  (item.field === 'email' || item.field === 'phone') && !lead?.email && !lead?.phone
                    ? 'Either Email or Phone is required'
                    : undefined
                }
              />
            )}
          </Grid>
        )})}
      </Grid>
      {children}
      </>
    );
    return (
      <UiDetailSection
        title={title}
        legacy={
          <MDBox
            mb={3}
            p={3}
            borderRadius="lg"
            sx={{
              backgroundColor: '#fffff',
              boxShadow: 3
            }}
          >
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <MDTypography variant="h6" fontWeight="medium" color="dark">{title}</MDTypography>
            </MDBox>
            {sectionBody}
          </MDBox>
        }
      >
        {sectionBody}
      </UiDetailSection>
    );
  };

  const fetchActivities = async () => {
    // If no leadId, don't proceed
    if (!id) {
      console.log('No lead ID provided, skipping activity fetch');
      return;
    }

    if (!hasTasksModule) {
      setActivities([]);
      return;
    }

    try {
      const response = await axiosInstance.get('/activities/', {
        params: {
          parent_table: 'leads',
          parent_table_id: id
        }
      });

      console.log('Fetched lead activities:', response.data);
      setActivities(response.data || []);

    } catch (error) {
      // Subscription without TASKS returns MODULE_NOT_ENTITLED — do not toast/crash
      if (!isModuleNotEntitledError(error)) {
        console.error('Error fetching activities:', error);
        setSnackbar({
          open: true,
          message: getApiErrorMessage(error, 'Error fetching activities'),
          severity: 'error',
        });
      }
      setActivities([]);
    }
  };

  const handleOpenDeleteModal = () => {
    setIsDeleteLoading(true); // Start loading
    setTimeout(() => {
      setIsDeleteModalOpen(true);
      setIsDeleteLoading(false); // Stop loading once modal opens
    }, 100); // Small delay for UI feedback
  };
  const handleCloseDeleteModal = () => setIsDeleteModalOpen(false);

  const handleDelete = async () => {
    if (isSupportUser) {
      setSnackbar({
        open: true,
        message: 'Support users are not allowed to delete leads.',
        severity: 'error',
      });
      return; // Exit the function early
    }
    setIsDeleteLoading(true); // Start loading
    handleCloseDeleteModal();
    try {

      await axiosInstance.delete(`/leads/${lead.id}`, {
        params: { delete_related: deleteRelated }
      });

      // Log lead deletion
      await frontendLogger.logDelete('Lead', {
        id: lead.id,
        name: lead.full_lead_name || lead.name,
        user: user.username,
        deleted_by: user.id
      });

      navigate('/leads/');
    } catch (error) {
      console.error('Error deleting lead:', error);

      // Log error
      await frontendLogger.logError(error, {
        context: 'Lead deletion',
        leadId: lead.id,
        user: user?.username,
        action: 'delete',
        timestamp: new Date().toISOString(),
      });

      setSnackbar({
        open: true,
        message: 'Error deleting lead. Please try again.',
        severity: 'error',
      });
    } finally {
      setIsDeleteLoading(false);
    }
  };

  const handleAgentClick = (agentId) => {
    if (agentId) {
      navigate(`/users/${agentId}/profile`);
    } else {
      setSnackbar({
        open: true,
        message: 'This lead is not assigned to any agent.',
        severity: 'info',
      });
    }
  };

  const fetchLeadData = async () => {
    setIsDetailsLoading(true);
    try {
      const response = await axiosInstance.get(`/leads/${id}`);
      console.log('Fetched lead:', response.data);
      // Debug: Log the country field
      if (!('country' in response.data) || response.data.country === undefined) {
        console.warn('Country field is missing or undefined in lead data:', response.data);
      } else {
        console.log('Country field value:', response.data.country);
      }
      setLead(response.data);

      if (response.data.lead_status === 'Qualified' || response.data.lead_status === 'Disqualified') {
        setFinalState(response.data.lead_status);
      } else {
        setFinalState(null);
      }

      const fetched_Step = getStepIndex(response.data.lead_status);
      setActiveStep(fetched_Step ? fetched_Step : 0);

      // Fetch feed items after lead data is loaded
      await fetchFeedItems();
    } catch (error) {
      console.error('Error fetching lead data:', error);
      if (error.response?.status === 403 && !isModuleNotEntitledError(error)) {
        console.error('You don\'t have permission to access this lead');
        setIsUnauthorized(true);
        setSnackbar({
          open: true,
          message: "You don't have permission to access this lead",
          severity: 'error',
        });
        // Redirect to leads list page after a short delay
        setTimeout(() => {
          navigate('/leads');
        }, 3000);
      } else if (!isModuleNotEntitledError(error)) {
        setSnackbar({
          open: true,
          message: 'Error fetching lead data. Please try again later.',
          severity: 'error',
        });
      }
    } finally {
      setIsDetailsLoading(false);
    }
  };

  const getStepIndex = (status) => {
    return steps.indexOf(status);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  const handleOpenActivityModal = () => {
    if (isLeadLocked) {
      setSnackbar({
        open: true,
        message: `Cannot add activities to ${lead.lead_status.toLowerCase()} leads`,
        severity: 'warning'
      });
      return;
    }
    setIsActivityModalOpen(true);
  };

  const handleCloseActivityModal = () => {
    setIsActivityModalOpen(false);
    setNewActivity({
      lead_id: id,
      type: '',
      description: '',
      date: new Date().toISOString().split('T')[0],
    });
  };

  const applyLeadStatusUpdate = async (stepIndex) => {
    const newStatus = steps[stepIndex];
    if (!newStatus || newStatus === lead.lead_status) return;

    await frontendLogger.logUpdate('Lead', {
      id: lead.id,
      action: 'stepper_change',
      old_status: lead.lead_status,
      new_status: newStatus,
      user: user.username,
      timestamp: new Date().toISOString(),
    });

    setIsDetailsLoading(true);
    try {
      const response = await axiosInstance.patch(`/leads/${lead.id}`, {
        lead_status: newStatus,
      });

      setLead(response.data);
      setActiveStep(stepIndex);

      if (newStatus === 'Disqualified') {
        setFinalState(newStatus);
      } else {
        setFinalState(null);
      }

      setSnackbar({
        open: true,
        message: `Lead status updated to ${newStatus}`,
        severity: 'success',
      });
    } catch (error) {
      console.error('Error updating lead status:', error);

      await frontendLogger.logError(error, {
        context: 'Lead status update via stepper',
        leadId: lead.id,
        oldStatus: lead.lead_status,
        newStatus: newStatus,
        user: user?.username,
        action: 'stepper_change',
        timestamp: new Date().toISOString(),
      });

      setSnackbar({
        open: true,
        message: 'Error updating lead status. Please try again.',
        severity: 'error',
      });
      throw error;
    } finally {
      setIsDetailsLoading(false);
    }
  };

  const handleStepClick = async (stepIndex) => {
    const newStatus = steps[stepIndex];
    if (newStatus === lead.lead_status) return;

    if (newStatus === 'Qualified') {
      handleQualificationClick();
      return;
    }

    setStatusStepConfirm({ open: true, stepIndex });
  };

  const handleStatusStepConfirmCancel = () => {
    if (isStatusStepConfirmLoading) return;
    setStatusStepConfirm({ open: false, stepIndex: null });
  };

  const handleStatusStepConfirm = async () => {
    const stepIndex = statusStepConfirm.stepIndex;
    if (stepIndex == null) {
      handleStatusStepConfirmCancel();
      return;
    }
    setIsStatusStepConfirmLoading(true);
    try {
      await applyLeadStatusUpdate(stepIndex);
      setStatusStepConfirm({ open: false, stepIndex: null });
    } catch {
      // Keep dialog open; snackbar already shown
    } finally {
      setIsStatusStepConfirmLoading(false);
    }
  };

  const handleQualificationClick = () => {
    setIsQualificationModalOpen(true);
  };

  const handleConvertToContactClick = () => {
    setIsConvertToContactOpen(true);
  };

  const handleConvertToContactConfirm = async () => {
    setIsConvertToContactLoading(true);
    try {
      const { data } = await axiosInstance.post(`/leads/${id}/convert-to-contact`);
      const contact = data?.contact;
      if (contact?.id) {
        await frontendLogger.logCreate('Contact', {
          id: contact.id,
          first_name: contact.first_name,
          last_name: contact.last_name,
          email: contact.email,
          created_by: user.id,
          user: user.username,
          created_at: contact.created_at || new Date().toISOString(),
          source: 'lead_convert_to_contact',
          lead_id: id,
        });
      }
      setLead((prev) => ({
        ...prev,
        contact_id: contact?.id ?? prev.contact_id,
        contact_name:
          [contact?.first_name, contact?.last_name].filter(Boolean).join(' ') ||
          prev.contact_name,
        account_id: data?.account_id ?? prev.account_id,
      }));
      setSnackbar({
        open: true,
        message: 'Lead converted to contact successfully (no deal created).',
        severity: 'success',
      });
      setTimeout(() => {
        if (contact?.id) navigate(`/contacts/${contact.id}`);
      }, 1200);
    } catch (error) {
      console.error('Error converting lead to contact:', error);
      throw error;
    } finally {
      setIsConvertToContactLoading(false);
    }
  };

  const handleActivitySaved = async (newActivityData) => {
    setIsActivitiesLoading(true);
    try {
      const currentDate = new Date().toISOString();
      setActivities([...activities, newActivityData]);

      // Log activity creation
      await frontendLogger.logCreate('Activity', {
        id: newActivityData.id,
        parent_table: 'leads',
        parent_table_id: id,
        action: 'create',
        user: user.username,
        timestamp: new Date().toISOString(),
      });

      // Fetch only the activities instead of all lead data
      await fetchActivities();

      setSnackbar({
        open: true,
        message: 'Activity created successfully!',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error updating activity:', error);
      setSnackbar({
        open: true,
        message: 'Error updating activity',
        severity: 'error',
      });
    } finally {
      setIsActivitiesLoading(false);
    }
  };

  const handleDeleteActivity = async (activityId) => {
    setIsLoading(true);
    try {
      await axiosInstance.delete(`/activities/${activityId}`);
      setActivities(activities.filter((activity) => activity.id !== activityId));
      setSnackbar({
        open: true,
        message: 'Activity deleted successfully!',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error deleting activity:', error);

      // Log error
      await frontendLogger.logError(error, {
        context: 'Activity deletion',
        activityId: activityId,
        leadId: id,
        user: user?.username,
        action: 'delete',
        timestamp: new Date().toISOString(),
      });

      setSnackbar({
        open: true,
        message: 'Error deleting activity',
        severity: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditActivity = (updated) => {
    if (!updated?.id) return;
    setActivities((prev) =>
      prev.map((a) => (a.id === updated.id ? { ...a, ...updated } : a)),
    );
  };

  const handleUploadSuccess = async (uploadData) => {
    console.log('Upload successful:', uploadData);

    const uploadDataPayload = {
      file_name: uploadData.file.name,
      file_type: uploadData.file.type,
      file_size: uploadData.file.size,
      bucket_name: uploadData.bucket,
      storage_path: uploadData.path,
      description: uploadData.description || '', // Optional, use default if no description is provided
      parent_table: 'leads',  // Assuming the parent table is 'leads', modify as needed
      parent_id: id,  // Assuming the parent ID is the same as lead ID
      is_active: true,
      is_private: uploadData.isPrivate || false, // Default to false if not provided
      file_url: uploadData.url,
      signed_url_expiry: uploadData.signedUrlExpiry || null,
    };

    setIsAttachmentModalOpen(false);
    try {
      const response = await axiosInstance.post(`/attachments/`, uploadDataPayload);

      // Log attachment creation
      await frontendLogger.logCreate('Attachment', {
        id: response.data.id,
        parent_table: 'leads',
        parent_table_id: id,
        action: 'create',
        user: user.username,
        timestamp: new Date().toISOString(),
      });

      setSnackbar({
        open: true,
        message: 'File uploaded successfully!',
        severity: 'success',
      });

    } catch (error) {
      console.error('Error uploading file:', error);

      // Log error
      await frontendLogger.logError(error, {
        context: 'File upload',
        leadId: id,
        fileName: uploadData.file.name,
        user: user?.username,
        action: 'upload',
        timestamp: new Date().toISOString(),
      });

      setSnackbar({
        open: true,
        message: 'Error uploading file',
        severity: 'error',
      });
    } finally {
      fetchAttachments();
    }
  };
  const handleDiscardChanges = async () => {
    setIsCancelLoading(true); // Start loading
    try {
      setIsEditing(false);
      setUpdatedFields({});
      setFormErrors({});
      await fetchLeadData();
      await fetchActivities();
      await fetchAttachments();
      setSnackbar({
        open: true,
        message: 'Changes discarded',
        severity: 'info',
      });
    } catch (error) {
      console.error('Error discarding changes:', error);
      setSnackbar({
        open: true,
        message: 'Error discarding changes',
        severity: 'error',
      });
    } finally {
      setIsCancelLoading(false); // Stop loading
    }
  };

  // Fetch feed items for this entity
  const fetchFeedItems = async () => {
    try {
      setIsFeedLoading(true);
      console.log('Fetching feed items for:', { entityType, entityId });
      const response = await axiosInstance.get(`/feed-items/entity/${entityType}/${entityId}`);
      console.log('Fetched feed items:', response.data);
      console.log('Feed items count:', response.data?.length || 0);

      // Debug: Log the first feed item structure
      if (response.data && response.data.length > 0) {
        console.log('First feed item structure:', response.data[0]);
        console.log('created_by_user data:', response.data[0].created_by_user);
      }

      setFeedItems(response.data || []);
    } catch (error) {
      console.error('Error fetching feed items:', error);
      setFeedItems([]);
      // Don't show error snackbar for feed items as it's not critical
    } finally {
      setIsFeedLoading(false);
    }
  };

  // Handler for when a new message is posted
  const handleEchoSubmit = async (feedItem) => {
    // The feedItem is the response from the API after successful creation
    console.log('Feed item created:', feedItem);

    // Refresh the feed items to ensure consistency
    await fetchFeedItems();

    // The success message will be shown by the onSuccess callback
  };

  const handleEditFeedItem = async (editData) => {
    try {
      setIsEditingFeed(true);

      // Extract the feed item ID from the edit data
      const feedItemId = editData.feed_item_id;

      if (!feedItemId) {
        throw new Error('No feed item ID found in edit data');
      }

      console.log('=== EDIT FEED ITEM DEBUG ===');
      console.log('Feed Item ID:', feedItemId);
      console.log('Edit Data received:', editData);
      console.log('Object Type:', editData.object_type);
      console.log('Object ID:', editData.object_id);
      console.log('Body content:', editData.body);
      console.log('Mentions:', editData.mentions);

      // Make the PUT request to the correct endpoint with the feed item ID
      const response = await axiosInstance.put(`/feed-items/${feedItemId}`, {
        feeditem: {
          object_type: editData.object_type,
          object_id: editData.object_id,
          body: editData.body
          // Don't include mentions - backend will parse them from body automatically
        }
      });

      if (response.status === 200) {
        // Refresh the entire feed to get updated data and proper sorting
        await fetchFeedItems();

        setSnackbar({
          open: true,
          message: 'Message updated successfully!',
          severity: 'success',
        });

        // The EchoComposer will handle clearing the edit state via onSuccess callback
        // so we don't need to do it here
      }
    } catch (error) {
      console.error('Error updating feed item:', error);
      // setSnackbar({
      //   open: true,
      //   message: 'Error updating message',
      //   severity: 'error',
      // });
    } finally {
      setIsEditingFeed(false);
    }
  };

  const handleDeleteFeedItem = async (feedItemId) => {
    try {
      const response = await axiosInstance.delete(`/feed-items/${feedItemId}`);

      if (response.status === 200) {
        // Update the feed items state by removing the deleted item
        setFeedItems(prevItems => prevItems.filter(item => item.id !== feedItemId));

        setSnackbar({
          open: true,
          message: 'Message deleted successfully!',
          severity: 'success',
        });
      }
    } catch (error) {
      console.error('Error deleting feed item:', error);
      setSnackbar({
        open: true,
        message: 'Error deleting message',
        severity: 'error',
      });
    }
  };

  const handleDeleteConfirm = (feedItem) => {
    setItemToDelete(feedItem);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setItemToDelete(null);
  };

  const handleDeleteConfirmOk = async () => {
    if (itemToDelete) {
      await handleDeleteFeedItem(itemToDelete.id);
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
    }
  };

  const startEditFeedItem = (feedItem) => {
    setEditingFeedItem(feedItem.id);
    setEditFeedContent(feedItem.feeditem.body);
  };

  const cancelEditFeedItem = () => {
    setEditingFeedItem(null);
    setEditFeedContent('');
  };

  const handleOpenEchoModal = () => {
    setIsEchoModalOpen(true);
  };

  const handleCloseEchoModal = () => {
    setIsEchoModalOpen(false);
  };

  const handleCreateEcho = async () => {
    // This will be handled by EchoComposer's onSubmit
    handleCloseEchoModal();
  };

  // Component for rendering mentions with immediate name display
  const MentionDisplay = ({ mention, fullMatch, entityTypeOrUser, entityId, mentions, navigate }) => {
    // Calculate display name immediately without async operations
    let displayName = '';

    if (entityId) {
      // For entity mentions, we'll need to fetch the name
      // But for now, let's show the entity type and ID
      displayName = `${entityTypeOrUser}:${entityId}`;
    } else {
      // For user mentions, use entity_name immediately if available
      if (mention && mention.entity_name) {
        displayName = mention.entity_name;
      } else if (mention && mention.first_name && mention.last_name) {
        displayName = `${mention.first_name} ${mention.last_name}`;
      } else if (mention && mention.username) {
        displayName = mention.username;
      } else {
        // Fallback to the original username from the text
        displayName = entityTypeOrUser;
      }
    }

    return (
      <Box
        component="span"
        sx={{
          color: entityId ? 'info.main' : 'primary.main',
          fontWeight: 'bold',
          backgroundColor: entityId ? 'rgba(33, 150, 243, 0.08)' : 'rgba(103, 58, 183, 0.08)',
          px: 0.5,
          py: 0.25,
          borderRadius: 0.5,
          mx: 0.25,
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: entityId ? 'rgba(33, 150, 243, 0.15)' : 'rgba(103, 58, 183, 0.15)',
          }
        }}
        onClick={() => {
          if (entityId) {
            // Navigate to the mentioned entity
            navigate(`/${entityTypeOrUser.toLowerCase()}s/${entityId}`);
          } else {
            // Handle user mention - navigate to user profile
            if (mention && mention.entity_id) {
              navigate(`/users/${mention.entity_id}/profile`);
            } else if (mention && mention.id) {
              navigate(`/users/${mention.id}/profile`);
            }
          }
        }}
      >
        {displayName}
      </Box>
    );
  };

  // Enhanced feed content parser that uses async name fetching
  const parseFeedContent = (feedItem) => {
    if (!feedItem?.feeditem?.body) return '';

    const body = feedItem.feeditem.body;
    const mentions = feedItem.feeditem.mentions || [];

    console.log('Parsing feed content:', { body, mentions });

    // Parse mentions in the format @username (for users) or @EntityType:entityId (for entities)
    const parts = [];
    const mentionRegex = /@([A-Za-z0-9_.@]+)(?::([A-Za-z0-9_]+))?/g;
    let lastIndex = 0;
    let match;

    // Create a map of usernames to mention data for better matching
    const userMentionsMap = {};
    mentions.forEach(mention => {
      if (mention.type === 'user' && mention.entity_name) {
        // Try to match by username pattern in the body
        const usernamePattern = new RegExp(`@${mention.entity_name.replace(/\s+/g, '[._]')}`, 'i');
        if (usernamePattern.test(body)) {
          userMentionsMap[mention.entity_name] = mention;
        }
      }
    });

    while ((match = mentionRegex.exec(body)) !== null) {
      // Add text before mention
      if (match.index > lastIndex) {
        parts.push(
          <span key={`text-${lastIndex}`}>
            {body.substring(lastIndex, match.index)}
          </span>
        );
      }

      // Add mention as highlighted text with async name fetching
      const [fullMatch, firstPart, secondPart] = match;

      // Determine if this is a user mention or entity mention
      let entityTypeOrUser, entityId;

      if (secondPart) {
        // Format: @EntityType:entityId
        entityTypeOrUser = firstPart;
        entityId = secondPart;
      } else {
        // Format: @username (user mention)
        entityTypeOrUser = firstPart;
        entityId = null;
      }

      // Find the corresponding mention data
      let mentionData;
      if (entityId) {
        // For entity mentions: @EntityType:entityId
        mentionData = mentions.find(m => m.type === 'record' && m.entity_type === entityTypeOrUser && m.entity_id === entityId);
      } else {
        // For user mentions: @username - try multiple matching strategies
        // First, try exact match with entity_name
        mentionData = mentions.find(m => m.type === 'user' && m.entity_name === entityTypeOrUser);

        // If not found, try matching by username pattern
        if (!mentionData) {
          const userMention = mentions.find(m => {
            if (m.type === 'user' && m.entity_name) {
              // Check if the username in text matches the entity_name (with possible dots/underscores)
              const normalizedEntityName = m.entity_name.replace(/\s+/g, '[._]');
              const usernamePattern = new RegExp(`^${normalizedEntityName}$`, 'i');
              return usernamePattern.test(entityTypeOrUser);
            }
            return false;
          });
          if (userMention) {
            mentionData = userMention;
          }
        }

        // If still not found, try to find by position in the mentions array
        if (!mentionData) {
          const userMentions = mentions.filter(m => m.type === 'user');
          // Count how many user mentions we've seen so far in the text
          const textBeforeCursor = body.substring(0, match.index);
          const userMentionsBefore = (textBeforeCursor.match(/@[A-Za-z0-9_.@]+/g) || []).length;
          if (userMentionsBefore < userMentions.length) {
            mentionData = userMentions[userMentionsBefore];
          }
        }
      }

      parts.push(
        <MentionDisplay
          key={`mention-${match.index}`}
          mention={mentionData}
          fullMatch={fullMatch}
          entityTypeOrUser={entityTypeOrUser}
          entityId={entityId}
          mentions={mentions}
          navigate={navigate}
        />
      );

      lastIndex = match.index + fullMatch.length;
    }

    // Add remaining text
    if (lastIndex < body.length) {
      parts.push(
        <span key={`text-end`}>
          {body.substring(lastIndex)}
        </span>
      );
    }

    return parts;
  };

  // Helper function to extract name parts from created_by_name
  const extractNameParts = (createdByUser, createdByName) => {
    let nameParts = {
      firstName: 'U',
      middleName: '',
      lastName: '',
      avatarUrl: null
    };

    if (createdByUser?.first_name && createdByUser?.last_name) {
      nameParts = {
        firstName: createdByUser.first_name,
        middleName: createdByUser.middle_name || '',
        lastName: createdByUser.last_name,
        avatarUrl: createdByUser.avatar || null
      };
    } else if (createdByName) {
      const namePartsArray = createdByName.split(' ').filter(part => part.trim());
      if (namePartsArray.length === 1) {
        nameParts = {
          firstName: namePartsArray[0],
          middleName: '',
          lastName: '',
          avatarUrl: null
        };
      } else if (namePartsArray.length >= 2) {
        nameParts = {
          firstName: namePartsArray[0],
          middleName: namePartsArray.slice(1, -1).join(' '),
          lastName: namePartsArray[namePartsArray.length - 1],
          avatarUrl: null
        };
      }
    }

    return nameParts;
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <DataLoadingSpinner
          entityType="lead"
          title="Loading Lead Data"
          message="Please wait while we fetch the lead information..."
        />
        
      </DashboardLayout>
    );
  }

  // Check if the user has permission to access this lead
  if (isUnauthorized) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <UnauthorizedAccess />
        
      </DashboardLayout>
    );
  }



  // Shared by the classic and v2 layouts below: the same actions, field
  // sections and side column render in both.
  const renderLeadActions = (aiTone) => (
    <>
      {/* Renders nothing unless the tenant has the AI Assistant. */}
      {lead?.id ? <AiScoreChip objectApiName="leads" recordId={lead.id} tone={aiTone} /> : null}
      <CallButton
        phone={lead?.mobile || lead?.phone}
        parentTable="leads"
        parentId={lead?.id}
        leadId={lead?.id}
        variant="icon"
        tone="header"
      />
      {/* Renders nothing unless a queue is scoped to leads. */}
      {lead?.id ? (
        <AssignToQueue
          objectApiName="leads"
          recordId={lead.id}
          ownerQueueId={lead.owner_queue_id}
          onChanged={() => window.location.reload()}
        />
      ) : null}
      <LeadDetailButtons
        isEditing={isEditing}
        isLeadLocked={isLeadLocked}
        handleEditToggle={canEdit ? handleEditToggle : null}
        handleCloneLead={canCreate ? handleCloneLead : null}
        handleOpenDeleteModal={canDelete ? handleOpenDeleteModal : null}
        handleSaveChanges={handleSaveChanges}
        setIsEditing={setIsEditing}
        handleDiscardChanges={handleDiscardChanges}
        handleToggleEmailModal={handleToggleEmailModal}
        canConvertToContact={canConvertToContact}
        handleConvertToContact={handleConvertToContactClick}
        isConvertToContactLoading={isConvertToContactLoading}
        isEditLoading={isEditLoading}
        isCloneLoading={isCloneLoading}
        isDeleteLoading={isDeleteLoading}
        isSaveLoading={isSaveLoading}
        isCancelLoading={isCancelLoading}
        isEmailLoading={isEmailLoading}
      />
    </>
  );
  const leadDetailsBody = (
    <>
      {isDetailsLoading ? (
        <LeadDetailsSkeleton />
      ) : (
        <>
          {visibleSections.map(sectionLabel => {
            if (sectionLabel === 'Basic Information') {
              const basicSection = renderSection('Basic Information', [
                { label: "Salutation", field: "salutation", options: ["Mr.", "Mrs.", "Ms.", "Dr.", "Prof."] },
                // Hidden from core CRM UI (field kept for API/compat)
                { label: "Designation", field: "designation", hidden: true },
                { label: "First Name", field: "first_name", preserveCase: true },
                { label: "Last Name", field: "last_name", preserveCase: true },
                // Hidden from core CRM UI (field kept for API/compat)
                { label: "Company", field: "company_name", hidden: true },
                { label: "Email", field: "email" },
                { label: "Phone", field: "phone" },
                { label: "Mobile", field: "mobile" },
                { label: "Website", field: "website" },
                { label: "Industry", field: "industry", options: ["Advertising", "Banking", "Consulting", "Education", "Finance", "Government", "Healthcare", "Insurance", "Manufacturing", "Media", "Retail", "Technology", "Telecommunications", "Travel", "Other"] },
                ...((showTripField || hasProductsModule) ? [{
                  label: showTripField ? "Interested Packages" : "Interested Products",
                  field: "interested_products",
                  options: products.map(p => ({ value: p.name, label: p.name })),
                  isMultiSelect: true
                }] : []),
                { label: "Lead Status", field: "lead_status", options: ["New", "In Progress", "Qualified", "Disqualified"] },
                { label: "Lead Rating", field: "lead_rating", options: ["Cold", "Warm", "Hot"] },
                {
                  label: "Assigned To",
                  field: "assigned_to",
                },
                // Hidden from core CRM UI (field kept for API/compat)
                {
                  label: "Lead Score",
                  field: "lead_score",
                  hidden: true,
                },
                { label: "Lead Source", field: "lead_source", options: ["Website", "Social Media", "Campaign", "Email", "Reference", "Other", "AI Search", "WebSearch"] },
                // Hidden from core CRM UI (field kept for API/compat)
                { label: "UTM Key", field: "utm_key", hidden: true },
                {
                  label: "Trip",
                  field: "trip_id",
                  readOnly: true,
                  hidden: !showTripField,
                  renderValue: (val) => lead?.trip?.name || val || '-',
                },
                { label: "Website Reference", field: "website_reference" },
                {
                  label: "Type",
                  field: "type",
                  // Keep the current value when travel is off so Merchant/
                  // Guide/Creator leads don't show a blank Select.
                  options: (() => {
                    const base = showTripField
                      ? ["Customer", "Merchant", "Guide", "Creator"]
                      : ["Customer"];
                    const current = updatedFields.type ?? lead?.type;
                    if (current && !base.includes(current)) {
                      return [...base, current];
                    }
                    return base;
                  })(),
                },
                // Hidden from core CRM UI (field kept for API/compat)
                { label: "Creator Type", field: "creator_type", options: ["Travel", "Lifestyle"], hidden: true },
                { label: "Language", field: "language", options: ["English", "French", "Spanish", "Deutsch", "Italian"] },
              ]);
              if (!showPropertyRequirements) return basicSection;
              // Real Estate tenants: property requirements follow
              // the basic information.
              return (
                <React.Fragment key="basic-and-property">
                  {basicSection}
                  <LeadPropertyRequirements
                    values={{ ...lead, ...updatedFields }}
                    isEditing={isEditing}
                    readOnly={isLeadLocked}
                    onChange={(field, value) => handleFieldUpdate(field, value)}
                  />
                </React.Fragment>
              );
            }
            if (sectionLabel === 'Additional Details') {
              return renderSection('Additional Details', [
                { label: "Address", field: "address" },
                { label: "City", field: "city" },
                { label: "State", field: "state" },
                { label: "Country", field: "country" },
                {
                  label: "Postal Code",
                  field: "postal_code",
                  renderValue: (value) => value === "None" || value === null || value === undefined ? "" : value
                },
                { label: "Annual Revenue", field: "annual_revenue" },
                { label: "Currency", field: "currency", options: currencyOptions },
                { label: "Number of Employees", field: "number_of_employees" },
                { label: "Notes", field: "notes" },
              ]);
            }
            if (sectionLabel === 'Custom Field Information') {
              return renderSection('Custom Field Information', [], renderCustomFields());
            }
            if (sectionLabel === 'Audit Information') {
              return renderSection('Audit Information', [
                { label: "Created By", field: "created_by_name", readOnly: true },
                { label: "Modified By", field: "updated_by_name", readOnly: true },
                { label: "Created Date", field: "created_at", readOnly: true },
                { label: "Modified Date", field: "updated_at", readOnly: true },
                { label: "Last Activity", field: "last_activity", readOnly: true },
              ]);
            }
            return null;
          })}
        </>
      )}
    </>
  );
  // Side-column pieces, named so the v2 record layout can place them in its
  // tabs and side column; leadSideColumn below keeps the classic order.
  const leadNbaCard = (
    <>
      {/* Renders nothing unless the user may use the AI Assistant. */}
      {lead?.id ? (
        <NextBestActions
          objectApiName="leads"
          recordId={lead.id}
          canAddTask={hasTasksModule && !isLeadLocked}
          onTaskCreated={() => fetchActivities()}
        />
      ) : null}
    </>
  );

  // Activities (needs the Tasks module).
  const leadActivitiesCard = (
    <>
      {hasTasksModule && (
      <Card>
        <MDBox
          mx={2}
          mt={-3}
          py={2}
          px={2}
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <EventIcon sx={{ color: 'white !important', mr: 1 }} />
            <MDTypography variant="h6" color="white">
              Activities
            </MDTypography>
          </Box>
          <MDButton
            variant="gradient"
            color="dark"
            onClick={handleOpenActivityModal}
            disabled={isLeadLocked}
            // startIcon={<AddIcon sx={{ fontSize: '1rem' }} />}
            sx={{
              '& .MuiButton-startIcon': {
                marginRight: '4px'
              },
              '&.Mui-disabled': {
                opacity: 0.6,
                backgroundColor: 'rgba(255, 255, 255, 0.12)',
                color: 'rgba(255, 255, 255, 0.7)',
              }
            }}
          >
            Add Activity
          </MDButton>
        </MDBox>

        <MDBox p={2}>
          {isActivitiesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={24} color="info" />
            </Box>
          ) : (
            <Activities
              activities={activities.filter(Boolean)}
              onDelete={handleDeleteActivity}
              onEdit={handleEditActivity}
              isLocked={isLeadLocked}
            />
          )}
        </MDBox>
      </Card>
      )}
    </>
  );

  // Attachments.
  const leadAttachmentsCard = (
    <>
      <Card>
        <MDBox
          mx={2}
          mt={-3}
          py={2}
          px={2}
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AttachmentIcon sx={{ color: 'white !important', mr: 1 }} />
            <MDTypography variant="h6" color="white">
              Attachments
            </MDTypography>
          </Box>
          <MDButton variant="gradient" color="dark" onClick={() => setIsAttachmentModalOpen(true)}>
            Add Attachment
          </MDButton>
        </MDBox>
        <MDBox p={2}>
          <Attachments
            attachments={attachments}
            onDelete={handleDeleteAttachment}
            onDownload={handleDownloadAttachment}
          />
        </MDBox>
      </Card>
    </>
  );

  // Call recordings and transcripts.
  const leadCallHistoryCard = (
    <>
      {/* Recordings and transcripts for this record. Gates itself on the
          CALL_CENTER entitlement, so no check is needed here. */}
      <CallHistoryCard recordType="lead" recordId={id} />
    </>
  );

  // Upload dialog for Attachments.
  const leadAttachmentUploadModal = (
    <>
      <AttachmentUploadModal
        isOpen={isAttachmentModalOpen}
        onClose={() => setIsAttachmentModalOpen(false)}
        onUpload={handleUploadSuccess}
        axiosInstance={axiosInstance}
        allowedFileTypes="*/*"
        maxSizeInMB={5}
      />
    </>
  );

  // Echo feed.
  const leadEchoFeedCard = (
    <>
      {licenseLoading ? (
        <Card>
          <MDBox
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="200px"
          >
            <CircularProgress size={32} color="info" />
          </MDBox>
        </Card>
      ) : !echoFeatureEnabled ? (

        <Card>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ opacity: 0.7 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <LockIcon sx={{ color: 'white !important', mr: 1 }} />
              <MDTypography variant="h6" color="white">
                Echo Feed - Disabled
              </MDTypography>
            </Box>
            <MDButton
              variant="gradient"
              color="dark"
              disabled
              sx={{ opacity: 0.5, cursor: 'not-allowed' }}
            >
              Add Message
            </MDButton>
          </MDBox>

          <MDBox p={2}>
            {/* Disabled overlay message */}
            <Box
              sx={{
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                border: '1px dashed rgba(0, 0, 0, 0.2)',
              }}
            >
              <LockIcon
                sx={{
                  fontSize: 48,
                  color: 'text.secondary',
                  opacity: 0.5,
                  mb: 2
                }}
              />
              <MDTypography variant="h6" color="text.secondary" gutterBottom>
                Feature Disabled
              </MDTypography>
              <MDTypography variant="body2" color="text.secondary">
                The Echo Feed feature has been disabled by your administrator.
              </MDTypography>
              <MDTypography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Please contact your admin for more information.
              </MDTypography>
            </Box>

            {/* Show existing messages in read-only mode with overlay */}
            {feedItems.length > 0 && (
              <Box mt={3} sx={{ position: 'relative' }}>
                {/* Overlay to prevent interaction */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                    zIndex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 1,
                  }}
                >
                  <Box sx={{ textAlign: 'center' }}>
                    <LockIcon sx={{ fontSize: 32, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="caption" color="text.disabled">
                      Read-only mode
                    </Typography>
                  </Box>
                </Box>

                {/* Existing messages - now non-interactive */}
                <Box
                  sx={{
                    maxHeight: 400,
                    overflowY: 'auto',
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    backgroundColor: '#fafafa',
                    opacity: 0.5,
                    pointerEvents: 'none', // Disable all interactions
                  }}
                >
                  {feedItems.map((item, index) => (
                    <Box
                      key={item.id}
                      sx={{
                        p: 2,
                        borderBottom: index < feedItems.length - 1 ? '1px solid #e0e0e0' : 'none',
                        backgroundColor: 'white',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                        {(() => {
                          const nameParts = extractNameParts(item.created_by_user, item.created_by_name);
                          return (
                            <MDUserAvatar
                              firstName={nameParts.firstName}
                              middleName={nameParts.middleName}
                              lastName={nameParts.lastName}
                              avatarUrl={nameParts.avatarUrl}
                              size={36}
                              variant="circular"
                            />
                          );
                        })()}
                        <Box sx={{ flex: 1, minWidth: 0, ml: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography
                                variant="subtitle2"
                                fontWeight="medium"
                                sx={{ color: 'text.secondary' }}
                              >
                                {item.created_by_user?.full_name || item.created_by_name}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ ml: 1 }}
                              >
                                {formatDate(item.updated_at, 'datetime')}
                              </Typography>
                            </Box>
                          </Box>
                          <Typography
                            variant="body2"
                            sx={{
                              whiteSpace: 'pre-wrap',
                              lineHeight: 1.5,
                              color: 'text.secondary'
                            }}
                          >
                            {parseFeedContent(item)}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </MDBox>
        </Card>
      ) : (


        <Card>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <RecordVoiceOverIcon sx={{ color: 'white !important', mr: 1 }} />
              <MDTypography variant="h6" color="white">
                Echo Feed
              </MDTypography>
            </Box>
            <MDButton
              variant="gradient" color="dark"
              onClick={handleOpenEchoModal}
            >
              Add Message
            </MDButton>

          </MDBox>


          <MDBox p={2}>
            {/* EchoComposer removed, replaced by modal */}
            <Modal
              open={isEchoModalOpen}
              onClose={handleCloseEchoModal}
              aria-labelledby="echo-modal-title"
              aria-describedby="echo-modal-description"
            >
              <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 600,
                bgcolor: 'background.paper',
                boxShadow: 24,
                borderRadius: 2,
                p: 4,
                display: 'flex',
                flexDirection: 'column',
              }}>
                <MDTypography id="echo-modal-title" variant="h6" mb={2}>
                  Post a Feed
                </MDTypography>
                <EchoComposer
                  onSubmit={handleEchoSubmit}
                  onCancel={handleCloseEchoModal}
                  placeholder="Type @ to mention users..."
                  axiosInstance={axiosInstance}
                  objectType="leads"
                  objectId={id}
                  onSuccess={(message) => {
                    setSnackbar({
                      open: true,
                      message: 'Feed item created successfully',
                      severity: 'success',
                    });
                    handleCloseEchoModal();
                  }}
                  onError={(message) => setSnackbar({
                    open: true,
                    message: `Error creating feed item: ${message}`,
                    severity: 'error',
                  })}
                />
              </Box>
            </Modal>
            {/* Display feed items */}
            <Box mt={3}>
              {isFeedLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress size={24} color="info" />
                </Box>
              ) : feedItems.length > 0 ? (
                <Box className="pearl-echo-feed" sx={{
                  maxHeight: 400,
                  overflowY: 'auto',
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  backgroundColor: '#fafafa'
                }}>
                  {feedItems.map((item, index) => (
                    <Box
                      key={item.id}
                      sx={{
                        p: 2,
                        borderBottom: index < feedItems.length - 1 ? '1px solid #e0e0e0' : 'none',
                        backgroundColor: 'white',
                        '&:hover': {
                          backgroundColor: '#f8f9fa'
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                        {(() => {
                          const nameParts = extractNameParts(item.created_by_user, item.created_by_name);
                          return (
                            <MDUserAvatar
                              firstName={nameParts.firstName}
                              middleName={nameParts.middleName}
                              lastName={nameParts.lastName}
                              avatarUrl={nameParts.avatarUrl}
                              size={36}
                              variant="circular"
                            />
                          );
                        })()}
                        <Box sx={{ flex: 1, minWidth: 0, ml: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography
                                variant="subtitle2"
                                fontWeight="medium"
                                sx={{
                                  color: 'primary.main',
                                  cursor: 'pointer',
                                  '&:hover': {
                                    textDecoration: 'underline'
                                  }
                                }}
                                onClick={() => {
                                  // Navigate to user profile
                                  const userMention = item.feeditem.mentions?.find(m => m.type === 'user' && m.entity_name === item.created_by_name);
                                  if (userMention) {
                                    navigate(`/users/${userMention.entity_id}/profile`);
                                  }
                                }}
                              >
                                {item.created_by_user?.full_name || item.created_by_name}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ ml: 1 }}
                              >
                                {formatDate(item.updated_at, 'datetime')}
                              </Typography>
                            </Box>

                            {/* Edit/Delete buttons - only show for current user's messages */}
                            {item.created_by === user?.id && (
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <Tooltip title="Edit message" arrow>
                                  <MDButton
                                    size="small"
                                    variant="text"
                                    color="info"
                                    onClick={() => startEditFeedItem(item)}
                                    disabled={isEditingFeed}
                                    sx={{ minWidth: 'auto', p: 0.5 }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </MDButton>
                                </Tooltip>
                                <Tooltip title="Delete message" arrow>
                                  <MDButton
                                    size="small"
                                    variant="text"
                                    color="error"
                                    onClick={() => handleDeleteConfirm(item)}
                                    disabled={isEditingFeed}
                                    sx={{ minWidth: 'auto', p: 0.5 }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </MDButton>
                                </Tooltip>
                              </Box>
                            )}
                          </Box>

                          {editingFeedItem === item.id ? (
                            <Box sx={{ mt: 1 }}>
                              <EchoComposer
                                value={editFeedContent}
                                onChange={setEditFeedContent}
                                placeholder="Edit your message..."
                                axiosInstance={axiosInstance}
                                objectType="leads"
                                objectId={id}
                                feedItemId={item.id}
                                isEditMode={true}
                                onSubmit={handleEditFeedItem}
                                onCancel={cancelEditFeedItem}
                                onSuccess={() => {
                                  // Auto-save when user clicks the Update button in EchoComposer
                                  setEditingFeedItem(null);
                                  setEditFeedContent('');
                                }}
                                sx={{
                                  mb: 1,
                                  '& .MuiInputBase-root': {
                                    backgroundColor: 'white'
                                  }
                                }}
                              />
                            </Box>
                          ) : (
                            <Typography
                              variant="body2"
                              sx={{
                                whiteSpace: 'pre-wrap',
                                lineHeight: 1.5,
                                color: 'text.primary'
                              }}
                            >
                              {parseFeedContent(item)}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mt: -2,
                }}>
                  <Box sx={{

                    width: 50,
                    height: 50,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(0,0,0,0.04)',
                    mb: 1.5
                  }}>
                    <RecordVoiceOverIcon sx={{ color: 'text.secondary', fontSize: 24, }} />
                  </Box>

                  <Typography variant="body2" sx={{ mb: 1 }}>

                    No messages yet
                  </Typography>
                  <Typography variant="caption">
                    Start a conversation by typing a message above
                  </Typography>
                </Box>
              )}
            </Box>
          </MDBox>
        </Card>
      )}
    </>
  );

  const leadSideColumn = (
    <>
      {leadNbaCard}
      {leadActivitiesCard}
      {hasTasksModule && <Box mt={5} />}
      {leadAttachmentsCard}
      <Box mt={5} />
      {leadCallHistoryCard}
      {leadAttachmentUploadModal}
      <Box mt={5} />
      {leadEchoFeedCard}
    </>
  );

  const leadStage = lead.lead_status || lead.status;
  const leadOwner = agents.find((agent) => agent.id === lead.assigned_to)?.fullname;
  // v2: the lead laid out like Account 360 (components/v2/record). The
  // pieces are the ones the classic page renders.
  const openLeadActivities = activities.filter((a) => a && !/complet|done|cancel/i.test(a.status || '')).length;
  const lastLeadActivity = activities.map((a) => a?.created_at).filter(Boolean).sort().pop();
  const leadPlace = [lead.city, lead.state, lead.country].filter(Boolean).join(', ');
  const v2Main = (
    <MDBox>
      <RecordPageV2
        sectionsLabel="Lead sections"
        hero={{
          label: 'Lead summary',
          icon: Target,
          eyebrow: 'Lead',
          title: lead.full_lead_name || '',
          animate: isCloning,
          actions: renderLeadActions('default'),
          badges: (
            <>
              {leadStage && <StatusBadge status={leadStage} />}
              {lead.lead_rating && <HeroBadge>{lead.lead_rating}</HeroBadge>}
              {lead.lead_source && <HeroBadge>{lead.lead_source}</HeroBadge>}
            </>
          ),
          meta: [
            { key: 'owner', icon: UserRound, content: leadOwner ? <>Assigned to <UserLink userId={lead.assigned_to} name={capitalizeFirstLetter(leadOwner)} /></> : 'Unassigned' },
            { key: 'company', icon: Building2, content: lead.company_name },
            { key: 'designation', icon: Briefcase, content: lead.designation },
            { key: 'place', icon: MapPin, content: leadPlace || null },
            { key: 'email', icon: Mail, content: lead.email ? linkedValue('Email', String(lead.email), lead.email, { showIcon: false }) : null },
            { key: 'phone', icon: Phone, content: (lead.mobile || lead.phone) ? linkedValue('Phone', String(lead.mobile || lead.phone), lead.mobile || lead.phone, { showIcon: false }) : null },
          ],
        }}
        tiles={[
          { key: 'rating', icon: Star, label: 'Rating', value: lead.lead_rating || '—' },
          lead.lead_score != null && lead.lead_score !== '' ? { key: 'score', icon: Gauge, label: 'Lead score', value: String(lead.lead_score) } : null,
          { key: 'source', icon: Megaphone, label: 'Source', value: lead.lead_source || '—' },
          hasTasksModule ? { key: 'open', icon: CheckSquare, label: 'Open activities', value: String(openLeadActivities), tab: 'activity' } : null,
          { key: 'last', icon: Clock, label: 'Last activity', value: relativeTime(lastLeadActivity) || 'None yet', tab: 'activity' },
          { key: 'created', icon: CalendarDays, label: 'Created', value: lead.created_at ? formatDate(lead.created_at, 'date') : '—' },
        ]}
        tabs={[
          {
            key: 'overview',
            label: 'Overview',
            icon: LayoutGrid,
            render: () => (
              <TabStack>
                <Card sx={{ p: 2.5 }}>
                  <ThemedCustomStepper activeStep={activeStep} steps={leadStatus} onStepClick={isLeadLocked ? undefined : handleStepClick} finalState={finalState} successStep="Qualified" failureStep="Disqualified" />
                </Card>
                {/* Each field section is its own card (DetailSection), so no wrapping Card. */}
                <MDBox ref={detailsRef}>
                  {leadDetailsBody}
                </MDBox>
              </TabStack>
            ),
          },
          {
            key: 'activity',
            label: 'Activity',
            icon: History,
            count: hasTasksModule ? activities.filter(Boolean).length : undefined,
            render: () => (
              <TabStack>
                {leadActivitiesCard}
                {leadEchoFeedCard}
                {leadCallHistoryCard}
              </TabStack>
            ),
          },
          { key: 'files', label: 'Files', icon: Paperclip, count: attachments.length, render: () => leadAttachmentsCard },
        ]}
        aside={(selectTab) => (
          <>
            {leadNbaCard}
            <ActivityTimeline activities={activities} feedItems={feedItems} onOpenAll={() => selectTab('activity')} emptyText="No activity on this lead yet." />
          </>
        )}
      />
      {leadAttachmentUploadModal}
    </MDBox>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {isV2 ? <RecordCallProvider value={{ parentTable: "leads", parentId: lead?.id, leadId: lead?.id }}>{v2Main}</RecordCallProvider> : (
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card
              sx={{
                animation: isCloning ? 'cloneAnimation 1s ease' : 'none',
              }}
            >
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDBox display="flex" justifyContent="space-between" alignItems="center">
                  <MDTypography
                    variant="h5"
                    color="white"
                    marginLeft={1}
                    marginBottom={2}
                    sx={{
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere',
                      lineHeight: 1.35,
                      pr: 2,
                    }}
                  >
                    {lead.full_lead_name || ""}
                  </MDTypography>

                  {/* Renders nothing when telephony is not configured. */}
                  <MDBox display="flex" alignItems="center" gap={1} mt={-1} mb={2}>
                  {renderLeadActions("header")}
                  </MDBox>

                </MDBox>
                <MDBox>
                  <MDTypography variant="button" color="white" mb={2}>
                    {lead.status}
                  </MDTypography>
                  <ThemedCustomStepper activeStep={activeStep} steps={leadStatus} onStepClick={isLeadLocked ? undefined : handleStepClick} finalState={finalState} successStep="Qualified" failureStep="Disqualified" />
                </MDBox>
              </MDBox>
              <MDBox p={3} ref={detailsRef}>
                {leadDetailsBody}
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            {leadSideColumn}
          </Grid>
        </Grid>
      </MDBox>
      )}

      <ActivityModal
        isOpen={isActivityModalOpen}
        onClose={handleCloseActivityModal}
        parentId={id}
        parent="leads"
        onActivitySaved={handleActivitySaved}
        currentUser={user}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        open={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        aria-labelledby="delete-modal-title"
        aria-describedby="delete-modal-description"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          boxShadow: 24,
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}>
          {/* Header */}
          <Box className="pearl-popup-header"
            sx={{
              backgroundColor: colors.popupHeader.background,
              px: 4,
              py: 3,
              position: 'sticky',
              top: 0,
              zIndex: 1,
            }}
          >
            <MDTypography
              id="delete-modal-title"
              variant="h6"
              component="h2"
              sx={{ color: '#FFFFFF' }}
            >
              Delete Lead
            </MDTypography>
          </Box>

          {/* Content */}
          <Box sx={{ p: 4 }}>
            <MDTypography
              id="delete-modal-description"
              variant="body1"
              mb={3}
              sx={{ color: '#34495e' }}
            >
              Are you sure you want to delete this lead? This action cannot be undone.
            </MDTypography>

            <FormControlLabel
              control={
                <Checkbox
                  checked={deleteRelated}
                  onChange={(e) => setDeleteRelated(e.target.checked)}
                  color="primary"
                />
              }
              label="Delete related records"
            />

            <MDBox display="flex" justifyContent="flex-end" mt={3}>
              <MDButton
                onClick={handleCloseDeleteModal}
                variant="outlined"
                color="secondary"
                sx={{ marginRight: 2 }}
              >
                Cancel
              </MDButton>
              <MDButton
                onClick={handleDelete}
                variant="gradient"
                color="error"
              >
                Delete
              </MDButton>
            </MDBox>
          </Box>
        </Box>
      </Modal>

      <StatusStepperConfirmDialog
        open={statusStepConfirm.open}
        title={
          statusStepConfirm.stepIndex != null && steps[statusStepConfirm.stepIndex] === 'Disqualified'
            ? 'Disqualify Lead'
            : 'Change lead status'
        }
        message={
          statusStepConfirm.stepIndex != null && steps[statusStepConfirm.stepIndex] === 'Disqualified'
            ? 'Are you sure you want to mark this lead as Disqualified? This will lock the lead for further status changes.'
            : `Are you sure you want to mark this lead as ${statusStepConfirm.stepIndex != null ? steps[statusStepConfirm.stepIndex] : ''}?`
        }
        confirmColor={
          statusStepConfirm.stepIndex != null && steps[statusStepConfirm.stepIndex] === 'Disqualified'
            ? 'error'
            : 'primary'
        }
        loading={isStatusStepConfirmLoading}
        onCancel={handleStatusStepConfirmCancel}
        onConfirm={handleStatusStepConfirm}
      />

      {/* Feed Item Delete Confirmation Modal */}
      <Modal
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="feed-delete-modal-title"
        aria-describedby="feed-delete-modal-description"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          boxShadow: 24,
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}>
          {/* Header */}
          <Box className="pearl-popup-header"
            sx={{
              backgroundColor: colors.popupHeader.background,
              px: 4,
              py: 3,
              position: 'sticky',
              top: 0,
              zIndex: 1,
            }}
          >
            <MDTypography
              id="feed-delete-modal-title"
              variant="h6"
              component="h2"
              sx={{ color: '#FFFFFF' }}
            >
              Delete Message
            </MDTypography>
          </Box>

          {/* Content */}
          <Box sx={{ p: 4 }}>
            <MDTypography
              id="feed-delete-modal-description"
              variant="body1"
              mb={3}
              sx={{ color: '#34495e' }}
            >
              Are you sure you want to delete this message? This action cannot be undone.
            </MDTypography>

            <MDBox display="flex" justifyContent="flex-end" mt={3}>
              <MDButton
                onClick={handleDeleteCancel}
                variant="outlined"
                color="secondary"
                sx={{ marginRight: 2 }}
              >
                Cancel
              </MDButton>
              <MDButton
                onClick={handleDeleteConfirmOk}
                variant="gradient"
                color="error"
              >
                Delete
              </MDButton>
            </MDBox>
          </Box>
        </Box>
      </Modal>

      

      <LeadFormModal
        isOpen={isCloneModalOpen}
        onClose={() => setIsCloneModalOpen(false)}
        onSave={handleCloneSave}
        agents={agents}
        currencies={currencies}
        defaultCurrency={defaultCurrency}
        user={user}
        title="Clone Lead"
        // Email/phone/mobile are unique across leads/contacts/accounts — leave
        // them blank so the user must enter new values (matches Contact clone).
        initialData={
          lead
            ? {
                ...lead,
                lead_status: 'New',
                email: '',
                phone: '',
                mobile: '',
              }
            : null
        }
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {renderQualificationModal()}

      <ConvertLeadToContactModal
        open={isConvertToContactOpen}
        onClose={() => setIsConvertToContactOpen(false)}
        onConfirm={handleConvertToContactConfirm}
        leadId={id}
        axiosInstance={axiosInstance}
        leadStatus={lead?.lead_status}
      />

      <EmailModal
        open={isEmailModalOpen}
        onClose={() => setIsEmailModalOpen(false)}
        newEmail={newEmail}
        setNewEmail={setNewEmail}
        handleSend={handleSendEmail}
        isGenerating={isGeneratingEmail}
        isSending={isSendingEmail}
        errorMessage={emailError}
        emailContext={emailContext}
        setEmailContext={setEmailContext}
        handleGenerateEmail={handleGenerateEmail}
        showCc={showCc}
        showBcc={showBcc}
        toggleCc={toggleCc}
        toggleBcc={toggleBcc}
        handleQuillChange={handleQuillChange}
        modules={quillModules}
        formats={quillFormats}
        attachments={emailAttachments}
        setEmailAttachments={setEmailAttachments}
      />
    </DashboardLayout>
  );
}

export default LeadDetail;